import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import CreativeProjectPreviewShell
  from "./CreativeProjectPreviewShell";

import ChildrensBookManuscriptReadOnly
  from "./ChildrensBookManuscriptReadOnly";

import TextbookLiveChapterPageFlow
  from "./TextbookLiveChapterPageFlow";

import {
  TextbookEquationPreview,
  TextbookFigurePreview,
  TextbookTablePreview,
  textbookPlacedObjectLabels,
} from "./TextbookChapterWorkspace";

import {
  prepareTextbookRichManuscriptBlock,
} from "../utils/textbookRichManuscriptBridge";

import {
  buildTextbookFigureTextFlowUnits,
} from "../utils/textbookFigureTextFlow";

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

function exactText(value) {
  return value == null
    ? ""
    : String(value);
}

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

const REVIEW_INDEX_PAGE_SIZE =
  12;


const TEXTBOOK_REVIEW_TRIM_PROFILES =
  Object.freeze({
    "5.5_x_8.5_in": {
      widthInches: 5.5,
      heightInches: 8.5,
      label: "5.5 × 8.5 in",
    },
    "6_x_9_in": {
      widthInches: 6,
      heightInches: 9,
      label: "6 × 9 in",
    },
    "8.25_x_8.25_in": {
      widthInches: 8.25,
      heightInches: 8.25,
      label: "8.25 × 8.25 in",
    },
    "8.5_x_8.5_in": {
      widthInches: 8.5,
      heightInches: 8.5,
      label: "8.5 × 8.5 in",
    },
    "8_x_10_in": {
      widthInches: 8,
      heightInches: 10,
      label: "8 × 10 in",
    },
    "8.5_x_11_in": {
      widthInches: 8.5,
      heightInches: 11,
      label: "8.5 × 11 in",
    },
  });


export function textbookReviewLayoutPreview(
  formatPlan = {}
) {
  const trimSize =
    clean(
      formatPlan?.trimSize
    );

  const profile =
    TEXTBOOK_REVIEW_TRIM_PROFILES[
      trimSize
    ] || null;

  if (!profile) {
    return {
      ready: false,
      trimSize: "",
      label: "",
      maxWidth: 760,
      aspectRatio:
        undefined,
      shape: "portrait",
    };
  }

  const square =
    profile.widthInches ===
    profile.heightInches;

  return {
    ready: true,
    trimSize,
    label:
      profile.label,
    widthInches:
      profile.widthInches,
    heightInches:
      profile.heightInches,
    maxWidth:
      square
        ? 820
        : 760,
    aspectRatio:
      `${profile.widthInches} / ${profile.heightInches}`,
    shape:
      square
        ? "square"
        : "portrait",
  };
}

function textbookReviewPreviewPageGeometry(
  layoutPreview = {}
) {
  const pageWidth =
    Math.max(
      520,
      Number(
        layoutPreview?.maxWidth ||
        760
      )
    );

  const widthInches =
    Number(
      layoutPreview?.widthInches ||
      6
    );

  const heightInches =
    Number(
      layoutPreview?.heightInches ||
      9
    );

  const pageHeight =
    Math.round(
      pageWidth *
      (
        heightInches /
        widthInches
      )
    );

  const horizontalMargin =
    Math.max(
      42,
      Math.round(
        pageWidth * 0.075
      )
    );

  const verticalMargin =
    Math.max(
      46,
      Math.round(
        pageHeight * 0.07
      )
    );

  const pageGap = 28;

  const contentWidth =
    pageWidth -
    horizontalMargin * 2;

  const contentHeight =
    pageHeight -
    verticalMargin * 2;

  /*
   * The browser's multi-column fragmentation pitch must equal
   * one complete preview page plus the visible gutter.
   * Because each text column is only the content box width,
   * the column gap also contains both page-side margins.
   */
  const columnGap =
    horizontalMargin * 2 +
    pageGap;

  return {
    pageWidth,
    pageHeight,
    horizontalMargin,
    verticalMargin,
    pageGap,
    contentWidth,
    contentHeight,
    columnGap,
    pagePitch:
      pageWidth +
      pageGap,
  };
}


export function reviewBookMatter(
  bookMap = {},
  type = ""
) {
  return safeArray(
    bookMap?.items
  )
    .filter(
      (item) =>
        clean(
          item?.type
        ) === type &&
        !clean(
          item
            ?.parentItemId
        )
    )
    .map(
      (item) => ({
        itemId:
          clean(
            item?.itemId
          ),
        type,
        title:
          clean(
            item?.title
          ),
        manuscriptContent:
          item
            ?.manuscriptContent ||
          null,
      })
    )
    .filter(
      (item) =>
        item.itemId
    );
}


export function reviewChapters(bookMap = {}) {
  const items =
    safeArray(
      bookMap?.items
    )
      .map(
        (
          item,
          index
        ) => ({
          itemId:
            clean(
              item?.itemId
            ),
          type:
            clean(
              item?.type
            ),
          title:
            clean(
              item?.title
            ),
          parentItemId:
            clean(
              item?.parentItemId
            ),
          sourceIndex:
            index,
        })
      )
      .filter(
        (item) =>
          item.itemId
      );

  const partsById =
    new Map(
      items
        .filter(
          (item) =>
            item.type ===
            "part"
        )
        .map(
          (item) => [
            item.itemId,
            item,
          ]
        )
    );

  const sectionsByChapter =
    new Map();

  items
    .filter(
      (item) =>
        item.type ===
        "section" &&
        item.parentItemId
    )
    .forEach(
      (section) => {
        const rows =
          sectionsByChapter.get(
            section.parentItemId
          ) || [];

        rows.push(section);

        sectionsByChapter.set(
          section.parentItemId,
          rows
        );
      }
    );

  return items
    .filter(
      (item) =>
        item.type ===
        "chapter"
    )
    .map(
      (
        chapter,
        index
      ) => ({
        ...chapter,
        chapterNumber:
          index + 1,
        part:
          partsById.get(
            chapter.parentItemId
          ) || null,
        sections:
          (
            sectionsByChapter.get(
              chapter.itemId
            ) || []
          ).map(
            (
              section,
              sectionIndex
            ) => ({
              ...section,
              sectionNumber:
                `${index + 1}.${sectionIndex + 1}`,
            })
          ),
      })
    );
}

function chapterHasSavedWriting(
  result
) {
  const revision =
    Number(
      result
        ?.chapterDraft
        ?.revision || 0
    );

  const blocks =
    safeArray(
      result
        ?.chapterDraft
        ?.blocks
    );

  return (
    Number.isInteger(
      revision
    ) &&
    revision >= 1 &&
    blocks.length > 0
  );
}

function blockLabel(
  type,
  isFR
) {
  const labels = {
    heading: {
      en: "Heading",
      fr: "Titre",
    },
    learning_objectives: {
      en: "Learning objectives",
      fr: "Objectifs d’apprentissage",
    },
    definition: {
      en: "Definition",
      fr: "Définition",
    },
    worked_example: {
      en: "Worked example / case study",
      fr: "Exemple guidé / étude de cas",
    },
    exercise: {
      en: "Exercise / practice",
      fr: "Exercice / pratique",
    },
    reference: {
      en: "Reference / citation",
      fr: "Référence / citation",
    },
    equation: {
      en: "Equation / formula",
      fr: "Équation / formule",
    },
    table: {
      en: "Table",
      fr: "Tableau",
    },
    note: {
      en: "Note",
      fr: "Note",
    },
    summary: {
      en: "Chapter summary",
      fr: "Résumé du chapitre",
    },
  };

  return labels[type]
    ? (
        isFR
          ? labels[type].fr
          : labels[type].en
      )
    : "";
}

function blockPanelStyle(
  type
) {
  if (
    type ===
      "learning_objectives" ||
    type ===
      "summary"
  ) {
    return {
      padding: 14,
      borderRadius: 14,
      background:
        "#f0fdf4",
      border:
        "1px solid rgba(5,150,105,0.2)",
    };
  }

  if (
    [
      "worked_example",
      "exercise",
    ].includes(type)
  ) {
    return {
      padding: 14,
      borderRadius: 14,
      background:
        "#eff6ff",
      border:
        "1px solid rgba(37,99,235,0.18)",
    };
  }

  if (
    type ===
      "definition" ||
    type ===
      "note" ||
    type ===
      "reference"
  ) {
    return {
      padding: 14,
      borderRadius: 14,
      background:
        "#f8fafc",
      border:
        "1px solid #e2e8f0",
    };
  }

  if (
    type ===
      "heading"
  ) {
    return {
      padding:
        "8px 0 2px",
      fontSize: 19,
      fontWeight: 800,
    };
  }

  return {
    padding: "2px 0",
  };
}

function ReviewTextBlock({
  block,
  isFR,
  placedLabel = "",
  flowingParagraph = false,
  flowAnchorBlockId = "",
  flowContent = null,
  flowInsertions = [],
}) {
  const blockType =
    clean(
      block?.type
    );

  if (
    blockType ===
      "equation"
  ) {
    const label =
      blockLabel(
        blockType,
        isFR
      );

    return (
      <div
        style={{
          ...blockPanelStyle(
            blockType
          ),
          display: "grid",
          gap: 7,
          breakInside:
            "avoid-column",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing:
              "0.07em",
            textTransform:
              "uppercase",
            color: "#047857",
          }}
        >
          {label}
        </div>

        <TextbookEquationPreview
          equation={
            block?.equation
          }
          fallbackText={
            block?.text
          }
          isFR={isFR}
        />
      </div>
    );
  }

  if (
    blockType ===
      "table"
  ) {
    const label =
      clean(
        placedLabel
      ) ||
      blockLabel(
        blockType,
        isFR
      );

    return (
      <div
        data-textbook-review-table="true"
        style={{
          ...blockPanelStyle(
            blockType
          ),
          display: "grid",
          gap: 7,
        }}
      >
        <TextbookTablePreview
          table={
            block?.table
          }
          isFR={isFR}
          label={label}
        />
      </div>
    );
  }

  const prepared =
    prepareTextbookRichManuscriptBlock(
      block,
      block?.manuscriptContent
    );

  const value =
    prepared?.ok
      ? prepared.value
      : null;

  const label =
    blockLabel(
      clean(
        block?.type
      ),
      isFR
    );

  return (
    <div
      style={{
        ...blockPanelStyle(
          clean(
            block?.type
          )
        ),
        display:
          (
            flowingParagraph ||
            flowContent != null ||
            safeArray(
              flowInsertions
            ).length > 0
          ) &&
          blockType ===
            "paragraph"
            ? "block"
            : "grid",
        gap:
          (
            flowingParagraph ||
            flowContent != null ||
            safeArray(
              flowInsertions
            ).length > 0
          ) &&
          blockType ===
            "paragraph"
            ? undefined
            : 7,
      }}
    >
      {label ? (
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing:
              "0.07em",
            textTransform:
              "uppercase",
            color: "#047857",
          }}
        >
          {label}
        </div>
      ) : null}

      {value ? (
        <ChildrensBookManuscriptReadOnly
          content={
            value
              .manuscriptContent
          }
          fallbackText={
            value.text
          }
          lang={
            isFR
              ? "fr"
              : "en"
          }
          flowAnchorBlockId={
            flowAnchorBlockId
          }
          flowContent={
            flowContent
          }
          flowInsertions={
            flowInsertions
          }
          embeddedFlow={true}
        />
      ) : (
        <div
          style={{
            whiteSpace:
              "pre-wrap",
            lineHeight: 1.65,
          }}
        >
          {exactText(
            block?.text
          )}
        </div>
      )}
    </div>
  );
}

const TEXTBOOK_REVIEW_FIGURE_PLACEMENTS =
  new Set([
    "full_width",
    "half_left",
    "half_right",
    "small_left",
    "small_right",
  ]);

function reviewFigurePlacementStyle(
  value,
  flowing = false
) {
  const requested =
    clean(value);

  const placement =
    TEXTBOOK_REVIEW_FIGURE_PLACEMENTS
      .has(
        requested
      )
      ? requested
      : "full_width";

  if (
    placement ===
      "half_left" ||
    placement ===
      "half_right"
  ) {
    return {
      placement,
      width:
        "clamp(220px, 48%, 440px)",
      maxWidth:
        "100%",
      ...(flowing
        ? {
            float:
              placement ===
                "half_right"
                ? "right"
                : "left",
            margin:
              placement ===
                "half_right"
                ? "0 0 14px 18px"
                : "0 18px 14px 0",
          }
        : {
            marginLeft:
              placement ===
                "half_right"
                ? "auto"
                : 0,
            marginRight:
              placement ===
                "half_left"
                ? "auto"
                : 0,
          }),
    };
  }

  if (
    placement ===
      "small_left" ||
    placement ===
      "small_right"
  ) {
    return {
      placement,
      width:
        "clamp(160px, 32%, 300px)",
      maxWidth:
        "100%",
      ...(flowing
        ? {
            float:
              placement ===
                "small_right"
                ? "right"
                : "left",
            margin:
              placement ===
                "small_right"
                ? "0 0 12px 16px"
                : "0 16px 12px 0",
          }
        : {
            marginLeft:
              placement ===
                "small_right"
                ? "auto"
                : 0,
            marginRight:
              placement ===
                "small_left"
                ? "auto"
                : 0,
          }),
    };
  }

  return {
    placement,
    width: "100%",
    maxWidth: "100%",
    marginLeft: 0,
    marginRight: 0,
  };
}

function ReviewFigureBlock({
  block,
  figures,
  onLoadFigureFile,
  isFR,
  placedLabel = "",
  flowing = false,
  flowAnchorBlockId = "",
  eagerImages = false,
}) {
  const figureAssetId =
    clean(
      block
        ?.figureAssetId
    );

  const placementStyle =
    reviewFigurePlacementStyle(
      block?.figurePlacement,
      flowing
    );

  const asset =
    safeArray(
      figures
    ).find(
      (item) =>
        clean(
          item?.id
        ) ===
        figureAssetId
    ) || null;

  return (
    <figure
      className={
        flowing
          ? "ns-textbook-figure-flow-figure"
          : undefined
      }
      data-textbook-figure-placement={
        placementStyle.placement
      }
      data-textbook-figure-flow-anchor-id={
        flowing
          ? clean(
              flowAnchorBlockId
            )
          : ""
      }
      style={{
        marginTop:
          flowing
            ? 0
            : 10,
        marginBottom:
          flowing
            ? 0
            : 10,
        marginLeft:
          placementStyle
            .marginLeft,
        marginRight:
          placementStyle
            .marginRight,
        margin:
          placementStyle
            .margin,
        float:
          placementStyle
            .float,
        width:
          placementStyle.width,
        maxWidth:
          placementStyle.maxWidth,
        padding: 14,
        borderRadius: 14,
        border:
          "1px solid rgba(5,150,105,0.2)",
        background:
          "#f8fafc",
        breakInside:
          "avoid-column",
      }}
    >
      {asset ? (
        <TextbookFigurePreview
          asset={asset}
          onLoadFigureFile={
            onLoadFigureFile
          }
          isFR={isFR}
          eager={
            eagerImages
          }
        />
      ) : (
        <div
          role="status"
          style={{
            minHeight: 100,
            display: "grid",
            placeItems:
              "center",
            borderRadius: 12,
            border:
              "1px dashed rgba(100,116,139,0.35)",
            color: "#64748b",
            textAlign:
              "center",
            padding: 14,
          }}
        >
          {isFR
            ? "L’aperçu de cette Figure n’est pas disponible."
            : "This Figure preview is not available."}
        </div>
      )}

      {asset ? (
        <figcaption
          style={{
            marginTop: 9,
            display: "grid",
            gap: 4,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color: "#047857",
            }}
          >
            {clean(
              placedLabel
            ) ||
              (
                isFR
                  ? "Figure"
                  : "Figure"
              )}
          </span>

          {clean(
            asset?.caption
          ) ? (
            <strong
              style={{
                color: "#0f172a",
              }}
            >
              {asset.caption}
            </strong>
          ) : null}

          {clean(
            asset?.creatorOrSource
          ) ? (
            <span
              style={{
                color: "#475569",
              }}
            >
              {isFR
                ? "Source : "
                : "Source: "}
              {asset.creatorOrSource}
            </span>
          ) : null}

          {clean(
            asset?.rightsOrLicense
          ) ? (
            <span
              style={{
                color: "#475569",
              }}
            >
              {isFR
                ? "Droits : "
                : "Rights: "}
              {asset.rightsOrLicense}
            </span>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}

function ReviewFigureTextFlowUnit({
  unit,
  figures,
  onLoadFigureFile,
  isFR,
  placedObjectLabels = {},
  eagerImages = false,
}) {
  const figure =
    unit?.figure;

  if (
    [
      "rich_manuscript_figure_flow",
      "cursor_figure_flow",
    ].includes(
      unit?.kind
    )
  ) {
    return (
      <ReviewTextBlock
        block={
          unit.paragraph
        }
        isFR={isFR}
        flowAnchorBlockId={
          clean(
            unit.anchorId
          )
        }
        flowContent={
          <ReviewFigureBlock
            block={figure}
            figures={figures}
            onLoadFigureFile={
              onLoadFigureFile
            }
            isFR={isFR}
            placedLabel={
              placedObjectLabels[
                clean(
                  figure?.blockId
                )
              ] || ""
            }
            flowing
            eagerImages={
              eagerImages
            }
            flowAnchorBlockId={
              clean(
                unit.anchorId
              )
            }
          />
        }
        flowInsertions={
          (
            Array.isArray(
              unit
                ?.additionalFigureFlows
            )
              ? unit
                  .additionalFigureFlows
              : []
          ).map(
            (
              additional
            ) => ({
              anchorBlockId:
                clean(
                  additional
                    .anchorId
                ),
              content: (
                <ReviewFigureBlock
                  block={
                    additional
                      .figure
                  }
                  figures={
                    figures
                  }
                  onLoadFigureFile={
                    onLoadFigureFile
                  }
                  isFR={isFR}
                  placedLabel={
                    placedObjectLabels[
                      clean(
                        additional
                          ?.figure
                          ?.blockId
                      )
                    ] || ""
                  }
                  flowing
                  eagerImages={
                    eagerImages
                  }
                  flowAnchorBlockId={
                    clean(
                      additional
                        .anchorId
                    )
                  }
                />
              ),
            })
          )
        }
      />
    );
  }

  const paragraphs =
    safeArray(
      unit?.paragraphs
    );

  if (
    !figure ||
    paragraphs.length === 0
  ) {
    return null;
  }

  return (
    <div
      className="ns-textbook-figure-flow"
      data-textbook-figure-text-flow="true"
    >
      <ReviewFigureBlock
        block={figure}
        figures={figures}
        onLoadFigureFile={
          onLoadFigureFile
        }
        isFR={isFR}
        placedLabel={
          placedObjectLabels[
            clean(
              figure?.blockId
            )
          ] || ""
        }
        flowing
        eagerImages={
          eagerImages
        }
      />

      {paragraphs.map(
        (paragraph) => (
          <div
            key={
              clean(
                paragraph
                  ?.blockId
              )
            }
            className="ns-textbook-figure-flow-paragraph"
          >
            <ReviewTextBlock
              block={paragraph}
              isFR={isFR}
              flowingParagraph
            />
          </div>
        )
      )}
    </div>
  );
}


export function TextbookPublicationProofChapterPages({
  item,
  result,
  formatPlan,
  onLoadFigureFile,
  isFR = false,
}) {
  const blocks =
    safeArray(
      result
        ?.chapterDraft
        ?.blocks
    );

  const figures =
    safeArray(
      result?.figureAssets
    );

  const placedObjectLabels =
    useMemo(
      () =>
        textbookPlacedObjectLabels(
          blocks,
          safeArray(
            item?.sections
          ),
          figures,
          isFR
        ),
      [
        blocks,
        item?.sections,
        figures,
        isFR,
      ]
    );

  if (result?.loading) {
    return (
      <div
        role="status"
        className="ns-card ns-card-soft"
        style={{
          padding: 16,
          borderRadius: 14,
        }}
      >
        {isFR
          ? "Chargement du chapitre enregistré…"
          : "Loading saved Chapter…"}
      </div>
    );
  }

  if (result?.error) {
    return (
      <div
        role="alert"
        style={{
          padding: 16,
          borderRadius: 14,
          background: "#fef2f2",
          color: "#991b1b",
        }}
      >
        {result.error}
      </div>
    );
  }

  if (!chapterHasSavedWriting(result)) {
    return (
      <div
        className="ns-card ns-card-soft"
        style={{
          padding: 16,
          borderRadius: 14,
        }}
      >
        <strong>
          {isFR
            ? "Aucun texte de chapitre enregistré."
            : "No saved Chapter writing yet."}
        </strong>
      </div>
    );
  }

  return (
    <TextbookLiveChapterPageFlow
      chapterTitle={
        clean(item?.title) ||
        (
          isFR
            ? "Chapitre sans titre"
            : "Untitled Chapter"
        )
      }
      blocks={blocks}
      sections={
        safeArray(
          item?.sections
        )
      }
      sectionLayout={
        safeArray(
          result
            ?.chapterDraft
            ?.sectionLayout
        )
      }
      formatPlan={
        formatPlan || {}
      }
      isFR={isFR}
      canEdit={false}
      editMode={false}
      proofMode={true}
      renderBlock={(block) =>
        clean(block?.type) ===
        "figure" ? (
          <ReviewFigureBlock
            block={block}
            figures={figures}
            onLoadFigureFile={
              onLoadFigureFile
            }
            isFR={isFR}
            placedLabel={
              placedObjectLabels[
                clean(
                  block?.blockId
                )
              ] || ""
            }
            eagerImages
          />
        ) : (
          <ReviewTextBlock
            block={block}
            isFR={isFR}
            placedLabel={
              placedObjectLabels[
                clean(
                  block?.blockId
                )
              ] || ""
            }
          />
        )
      }
      renderFlowUnit={(unit) => (
        <ReviewFigureTextFlowUnit
          unit={unit}
          figures={figures}
          onLoadFigureFile={
            onLoadFigureFile
          }
          isFR={isFR}
          placedObjectLabels={
            placedObjectLabels
          }
          eagerImages
        />
      )}
    />
  );
}


export function ReviewChapter({
  item,
  result,
  formatPlan,
  onOpenChapter,
  onLoadFigureFile,
  onPreviewPageCountChange,
  isFR,
}) {
  const blocks =
    safeArray(
      result
        ?.chapterDraft
        ?.blocks
    );

  const figures =
    safeArray(
      result?.figureAssets
    );

  const startOnNewPageSectionIds =
    new Set(
      safeArray(
        result
          ?.chapterDraft
          ?.sectionLayout
      )
        .filter(
          (entry) =>
            entry
              ?.startOnNewPage ===
            true
        )
        .map(
          (entry) =>
            clean(
              entry
                ?.sectionItemId
            )
        )
        .filter(Boolean)
    );

  const placedObjectLabels =
    useMemo(
      () =>
        textbookPlacedObjectLabels(
          blocks,
          safeArray(
            item?.sections
          ),
          figures,
          isFR
        ),
      [
        blocks,
        item?.sections,
        figures,
        isFR,
      ]
    );

  const layoutPreview =
    textbookReviewLayoutPreview(
      formatPlan
    );

  const previewGeometry =
    textbookReviewPreviewPageGeometry(
      layoutPreview
    );

  const previewFlowRef =
    useRef(null);

  const previewViewportRef =
    useRef(null);

  const [
    previewPageState,
    setPreviewPageState,
  ] = useState({
    pageCount: 1,
    pageIndex: 0,
  });

  const [
    sectionPageRanges,
    setSectionPageRanges,
  ] = useState({});

  const [
    activeReviewSectionId,
    setActiveReviewSectionId,
  ] = useState("");

  const sectionById =
    new Map(
      safeArray(
        item?.sections
      ).map(
        (section) => [
          clean(
            section?.itemId
          ),
          section,
        ]
      )
    );

  const reviewSections =
    useMemo(
      () => {
        const visibleSectionIds =
          new Set(
            blocks
              .map(
                (block) =>
                  clean(
                    block
                      ?.sectionItemId
                  )
              )
              .filter(Boolean)
          );

        return safeArray(
          item?.sections
        ).filter(
          (section) =>
            visibleSectionIds.has(
              clean(
                section?.itemId
              )
            )
        );
      },
      [
        blocks,
        item?.sections,
      ]
    );

  const chapterAnchorId =
    `textbook-review-chapter-${clean(
      item?.itemId
    )}`;

  useEffect(() => {
    setActiveReviewSectionId(
      ""
    );
    setSectionPageRanges({});
  }, [item?.itemId]);

  function jumpTo(
    anchorId
  ) {
    if (
      typeof document ===
        "undefined"
    ) {
      return;
    }

    const target =
      document.getElementById(
        anchorId
      );

    target?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }

  useEffect(() => {
    const flow =
      previewFlowRef.current;

    const viewport =
      previewViewportRef.current;

    if (
      !flow ||
      !viewport
    ) {
      return undefined;
    }

    let frame = null;
    let timer = null;
    let resizeObserver =
      null;

    const measureSectionPageRanges =
      (pageCount) => {
        const flowRect =
          flow
            .getBoundingClientRect?.();

        if (!flowRect) {
          return;
        }

        const nextRanges = {};

        Array.from(
          flow.querySelectorAll?.(
            "[data-textbook-review-unit-section-id]"
          ) || []
        ).forEach(
          (element) => {
            const sectionItemId =
              clean(
                element
                  .getAttribute?.(
                    "data-textbook-review-unit-section-id"
                  )
              );

            if (!sectionItemId) {
              return;
            }

            const fragmentRects =
              Array.from(
                element
                  .getClientRects?.() ||
                []
              );

            const rects =
              fragmentRects.length > 0
                ? fragmentRects
                : [
                    element
                      .getBoundingClientRect?.(),
                  ].filter(Boolean);

            rects.forEach(
              (rect) => {
                const relativeLeft =
                  Number(
                    rect?.left || 0
                  ) -
                  Number(
                    flowRect.left || 0
                  );

                const pageIndex =
                  Math.max(
                    0,
                    Math.min(
                      pageCount - 1,
                      Math.round(
                        relativeLeft /
                        previewGeometry
                          .pagePitch
                      )
                    )
                  );

                const current =
                  nextRanges[
                    sectionItemId
                  ];

                nextRanges[
                  sectionItemId
                ] =
                  current
                    ? {
                        startPage:
                          Math.min(
                            current.startPage,
                            pageIndex
                          ),
                        endPage:
                          Math.max(
                            current.endPage,
                            pageIndex
                          ),
                      }
                    : {
                        startPage:
                          pageIndex,
                        endPage:
                          pageIndex,
                      };
              }
            );
          }
        );

        setSectionPageRanges(
          (current) =>
            JSON.stringify(
              current
            ) ===
            JSON.stringify(
              nextRanges
            )
              ? current
              : nextRanges
        );
      };

    const updatePageCount =
      () => {
        const overflowWidth =
          Math.max(
            0,
            Number(
              flow.scrollWidth || 0
            ) -
            previewGeometry
              .contentWidth
          );

        const pageCount =
          Math.max(
            1,
            Math.round(
              overflowWidth /
              previewGeometry
                .pagePitch
            ) + 1
          );

        setPreviewPageState(
          (current) => ({
            pageCount,
            pageIndex:
              Math.min(
                current.pageIndex,
                pageCount - 1
              ),
          })
        );

        measureSectionPageRanges(
          pageCount
        );

        if (

          typeof onPreviewPageCountChange ===

            "function"

        ) {

          onPreviewPageCountChange(

            clean(item?.itemId),

            pageCount

          );

        }

      };

    const updateCurrentPage =
      () => {
        const pageIndex =
          Math.max(
            0,
            Math.min(
              previewPageState
                .pageCount - 1,
              Math.round(
                Number(
                  viewport.scrollLeft ||
                  0
                ) /
                previewGeometry
                  .pagePitch
              )
            )
          );

        setPreviewPageState(
          (current) => (
            pageIndex ===
              current.pageIndex
              ? current
              : {
                  ...current,
                  pageIndex,
                }
          )
        );
      };

    const scheduleUpdate =
      () => {
        if (
          typeof window !==
            "undefined" &&
          typeof window
            .requestAnimationFrame ===
            "function"
        ) {
          frame =
            window
              .requestAnimationFrame(
                updatePageCount
              );
        } else {
          updatePageCount();
        }
      };

    flow.addEventListener(
      "load",
      scheduleUpdate,
      true
    );

    viewport.addEventListener(
      "scroll",
      updateCurrentPage,
      {
        passive: true,
      }
    );

    if (
      typeof window !==
        "undefined"
    ) {
      window.addEventListener(
        "resize",
        scheduleUpdate
      );

      if (
        typeof window
          .ResizeObserver ===
          "function"
      ) {
        resizeObserver =
          new window.ResizeObserver(
            scheduleUpdate
          );

        resizeObserver.observe(
          flow
        );
      }
    }

    scheduleUpdate();

    timer =
      setTimeout(
        updatePageCount,
        240
      );

    return () => {
      flow.removeEventListener(
        "load",
        scheduleUpdate,
        true
      );

      viewport.removeEventListener(
        "scroll",
        updateCurrentPage
      );

      if (
        typeof window !==
          "undefined"
      ) {
        window.removeEventListener(
          "resize",
          scheduleUpdate
        );

        if (
          frame != null &&
          typeof window
            .cancelAnimationFrame ===
            "function"
        ) {
          window
            .cancelAnimationFrame(
              frame
            );
        }
      }

      if (timer != null) {
        clearTimeout(timer);
      }

      resizeObserver?.disconnect?.();
    };
  }, [
    blocks.length,
    figures.length,
    layoutPreview.trimSize,
    previewGeometry
      .contentWidth,
    previewGeometry
      .pagePitch,
    result
      ?.chapterDraft
      ?.revision,
    result?.loading,
    result?.error,
    item?.itemId,
    onPreviewPageCountChange,
    previewPageState
      .pageCount,
  ]);

  function goToPreviewPage(
    requestedIndex
  ) {
    const pageIndex =
      Math.max(
        0,
        Math.min(
          previewPageState
            .pageCount - 1,
          Number(
            requestedIndex
          ) || 0
        )
      );

    setPreviewPageState(
      (current) => ({
        ...current,
        pageIndex,
      })
    );

    previewViewportRef
      .current
      ?.scrollTo?.({
        left:
          pageIndex *
          previewGeometry
            .pagePitch,
        behavior: "smooth",
      });
  }

  function goToReviewSection(
    requestedSectionItemId
  ) {
    const sectionItemId =
      clean(
        requestedSectionItemId
      );

    setActiveReviewSectionId(
      sectionItemId
    );

    if (!sectionItemId) {
      goToPreviewPage(0);
      return;
    }

    const range =
      sectionPageRanges[
        sectionItemId
      ];

    if (
      range &&
      Number.isInteger(
        range.startPage
      )
    ) {
      goToPreviewPage(
        range.startPage
      );
      return;
    }

    jumpTo(
      `textbook-review-section-${sectionItemId}`
    );
  }

  const activeReviewSectionIndex =
    reviewSections.findIndex(
      (section) =>
        clean(
          section?.itemId
        ) ===
        clean(
          activeReviewSectionId
        )
    );

  const activeReviewSection =
    activeReviewSectionIndex >= 0
      ? reviewSections[
          activeReviewSectionIndex
        ]
      : null;

  const activeReviewSectionRange =
    activeReviewSection
      ? (
          sectionPageRanges[
            clean(
              activeReviewSection
                .itemId
            )
          ] || null
        )
      : null;

  const activeReviewSectionPageCount =
    activeReviewSectionRange
      ? (
          activeReviewSectionRange
            .endPage -
          activeReviewSectionRange
            .startPage +
          1
        )
      : 0;

  const activeReviewSectionPageNumber =
    (
      activeReviewSectionRange &&
      previewPageState.pageIndex >=
        activeReviewSectionRange
          .startPage &&
      previewPageState.pageIndex <=
        activeReviewSectionRange
          .endPage
    )
      ? (
          previewPageState
            .pageIndex -
          activeReviewSectionRange
            .startPage +
          1
        )
      : 0;

  useEffect(() => {
    if (
      reviewSections.length === 0
    ) {
      setActiveReviewSectionId(
        ""
      );
      return;
    }

    setActiveReviewSectionId(
      (currentSectionItemId) => {
        const currentRange =
          sectionPageRanges[
            clean(
              currentSectionItemId
            )
          ];

        if (
          currentRange &&
          previewPageState.pageIndex >=
            currentRange.startPage &&
          previewPageState.pageIndex <=
            currentRange.endPage
        ) {
          return currentSectionItemId;
        }

        const match =
          reviewSections.find(
            (section) => {
              const range =
                sectionPageRanges[
                  clean(
                    section?.itemId
                  )
                ];

              return Boolean(
                range &&
                previewPageState
                  .pageIndex >=
                  range.startPage &&
                previewPageState
                  .pageIndex <=
                  range.endPage
              );
            }
          );

        return clean(
          match?.itemId
        );
      }
    );
  }, [
    item?.itemId,
    previewPageState.pageIndex,
    reviewSections,
    sectionPageRanges,
  ]);

  const previewStripWidth =
    (
      previewPageState
        .pageCount *
      previewGeometry
        .pageWidth
    ) +
    (
      Math.max(
        0,
        previewPageState
          .pageCount - 1
      ) *
      previewGeometry
        .pageGap
    );

  const chapterOutline =
    reviewSections.length > 0
      ? (
          <div
            className="ns-card ns-card-soft"
            data-textbook-preview-chapter-outline="true"
            style={{
              maxWidth:
                previewGeometry
                  .pageWidth,
              margin:
                "0 auto 14px",
              padding: 14,
              borderRadius: 14,
            }}
          >
            <strong>
              {isFR
                ? "Plan du chapitre"
                : "Chapter outline"}
            </strong>

            <p
              className="ns-card-meta"
              style={{
                margin:
                  "5px 0 10px",
              }}
            >
              {isFR
                ? "Accédez directement à une section. Les commandes de page continuent normalement dans les sections longues."
                : "Jump directly to a Section. Preview page controls continue normally through long Sections."}
            </p>

            <div
              data-textbook-preview-section-navigator="true"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "auto minmax(0, 1fr) auto",
                alignItems: "end",
                gap: 8,
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  activeReviewSectionIndex <
                  0
                }
                onClick={() =>
                  goToReviewSection(
                    activeReviewSectionIndex <=
                      0
                      ? ""
                      : reviewSections[
                          activeReviewSectionIndex -
                          1
                        ]?.itemId
                  )
                }
              >
                {isFR
                  ? "Section précédente"
                  : "Previous Section"}
              </button>

              <label
                style={{
                  minWidth: 0,
                  display: "grid",
                  gap: 5,
                  fontSize: 12,
                  color: "#475569",
                  fontWeight: 800,
                }}
              >
                <span>
                  {isFR
                    ? "Aller à"
                    : "Jump to"}
                </span>

                <select
                  data-textbook-preview-section-jump="true"
                  aria-label={
                    isFR
                      ? "Aller directement à une section du chapitre"
                      : "Jump directly to a Chapter Section"
                  }
                  value={
                    activeReviewSectionId
                  }
                  onChange={(event) =>
                    goToReviewSection(
                      event.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    minHeight: 38,
                    padding:
                      "7px 34px 7px 10px",
                    borderRadius: 8,
                    border:
                      "1px solid #cbd5e1",
                    background:
                      "#ffffff",
                    color:
                      "#0f172a",
                    font:
                      "inherit",
                    fontWeight: 800,
                    cursor:
                      "pointer",
                  }}
                >
                  <option value="">
                    {isFR
                      ? "Début du chapitre"
                      : "Chapter start"}
                  </option>

                  {reviewSections.map(
                    (section) => (
                      <option
                        key={
                          section.itemId
                        }
                        value={
                          section.itemId
                        }
                      >
                        {`${section.sectionNumber} · ${
                          clean(
                            section.title
                          ) ||
                          (
                            isFR
                              ? "Sans titre"
                              : "Untitled"
                          )
                        }`}
                      </option>
                    )
                  )}
                </select>
              </label>

              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  activeReviewSectionIndex >=
                  reviewSections.length -
                    1
                }
                onClick={() =>
                  goToReviewSection(
                    reviewSections[
                      activeReviewSectionIndex +
                      1
                    ]?.itemId
                  )
                }
              >
                {isFR
                  ? "Section suivante"
                  : "Next Section"}
              </button>
            </div>

            <div
              data-textbook-preview-section-status="true"
              className="ns-card-meta"
              style={{
                marginTop: 9,
                lineHeight: 1.45,
                fontWeight: 750,
              }}
            >
              {activeReviewSection
                ? (
                    <>
                      {clean(
                        activeReviewSection
                          .title
                      ) ||
                        (
                          isFR
                            ? "Section sans titre"
                            : "Untitled Section"
                        )}
                      {" · "}
                      {isFR
                        ? `Section ${activeReviewSectionIndex + 1} sur ${reviewSections.length}`
                        : `Section ${activeReviewSectionIndex + 1} of ${reviewSections.length}`}

                      {activeReviewSectionRange
                        ? (
                            <>
                              {" · "}
                              {activeReviewSectionRange
                                .startPage ===
                              activeReviewSectionRange
                                .endPage
                                ? (
                                    isFR
                                      ? `Page d’aperçu ${activeReviewSectionRange.startPage + 1}`
                                      : `Preview page ${activeReviewSectionRange.startPage + 1}`
                                  )
                                : (
                                    isFR
                                      ? `Pages d’aperçu ${activeReviewSectionRange.startPage + 1}–${activeReviewSectionRange.endPage + 1}`
                                      : `Preview pages ${activeReviewSectionRange.startPage + 1}–${activeReviewSectionRange.endPage + 1}`
                                  )}

                              {activeReviewSectionPageNumber >
                              0 ? (
                                <>
                                  {" · "}
                                  {isFR
                                    ? `Page ${activeReviewSectionPageNumber} sur ${activeReviewSectionPageCount} dans cette section`
                                    : `Section page ${activeReviewSectionPageNumber} of ${activeReviewSectionPageCount}`}
                                </>
                              ) : null}
                            </>
                          )
                        : null}
                    </>
                  )
                : (
                    isFR
                      ? `Début du chapitre · ${reviewSections.length} sections`
                      : `Chapter start · ${reviewSections.length} Sections`
                  )}
            </div>
          </div>
        )
      : null;

  let previousSectionId =
    "";

  return (
    <div
      data-textbook-book-layout-preview="true"
      data-textbook-preview-trim={
        layoutPreview
          .trimSize
      }
      style={{
        padding:
          "18px clamp(10px, 3vw, 28px) 30px",
        borderRadius: 18,
        background:
          "#eef2f7",
      }}
    >
      <div
        style={{
          maxWidth:
            layoutPreview
              .maxWidth,
          margin:
            "0 auto 10px",
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-end",
          gap: 12,
          flexWrap:
            "wrap",
          color: "#475569",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color: "#0f766e",
            }}
          >
            {isFR
              ? "Aperçu de mise en page du livre"
              : "Book Layout Preview"}
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {layoutPreview
              .ready
              ? (
                  <>
                    {
                      layoutPreview
                        .label
                    }
                    {" · "}
                    {isFR
                      ? "Lecture seule"
                      : "Read-only"}
                  </>
                )
              : (
                  isFR
                    ? "Format du livre non encore sélectionné"
                    : "Book format not selected yet"
                )}
          </div>
        </div>

        <div
          style={{
            maxWidth: 420,
            fontSize: 12,
            lineHeight: 1.45,
            textAlign:
              "right",
          }}
        >
          {isFR
            ? "Les limites finales des pages seront dérivées pendant la préparation de publication."
            : "Final page boundaries are derived during publication preparation."}
        </div>
      </div>

      {chapterOutline}

      <div
        data-textbook-preview-page-navigation="true"
        style={{
          maxWidth:
            previewGeometry
              .pageWidth,
          margin:
            "0 auto 10px",
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          gap: 10,
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            previewPageState
              .pageIndex <= 0
          }
          onClick={() =>
            goToPreviewPage(
              previewPageState
                .pageIndex - 1
            )
          }
        >
          {isFR
            ? "Page d’aperçu précédente"
            : "Previous Preview Page"}
        </button>

        <label
          data-textbook-preview-page-status="true"
          style={{
            display:
              "inline-flex",
            alignItems:
              "center",
            gap: 7,
            fontSize: 13,
            color: "#334155",
            fontWeight: 800,
          }}
        >
          <span>
            {isFR
              ? "Page d’aperçu"
              : "Preview page"}
          </span>

          <select
            data-textbook-preview-page-jump="true"
            aria-label={
              isFR
                ? "Aller directement à une page d’aperçu"
                : "Jump directly to a preview page"
            }
            value={
              previewPageState.pageIndex
            }
            onChange={(event) =>
              goToPreviewPage(
                Number(
                  event.target.value
                )
              )
            }
            style={{
              minHeight: 36,
              minWidth: 112,
              padding:
                "6px 30px 6px 10px",
              borderRadius: 8,
              border:
                "1px solid #cbd5e1",
              background:
                "#ffffff",
              color:
                "#0f172a",
              font:
                "inherit",
              fontWeight: 800,
              cursor:
                "pointer",
            }}
          >
            {Array.from({
              length:
                previewPageState.pageCount,
            }).map(
              (
                _,
                pageIndex
              ) => (
                <option
                  key={
                    `matter-preview-page-jump-${pageIndex + 1}`
                  }
                  value={
                    pageIndex
                  }
                >
                  {isFR
                    ? `${pageIndex + 1} sur ${previewPageState.pageCount}`
                    : `${pageIndex + 1} of ${previewPageState.pageCount}`}
                </option>
              )
            )}
          </select>
        </label>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            previewPageState
              .pageIndex >=
            previewPageState
              .pageCount - 1
          }
          onClick={() =>
            goToPreviewPage(
              previewPageState
                .pageIndex + 1
            )
          }
        >
          {isFR
            ? "Page d’aperçu suivante"
            : "Next Preview Page"}
        </button>
      </div>

      <div
        ref={
          previewViewportRef
        }
        data-textbook-preview-page-viewport="true"
        style={{
          width: "100%",
          maxWidth:
            previewGeometry
              .pageWidth,
          height:
            previewGeometry
              .pageHeight,
          margin: "0 auto",
          overflowX: "auto",
          overflowY:
            "hidden",
          scrollBehavior:
            "smooth",
          scrollSnapType:
            "x mandatory",
        }}
      >
        <div
          style={{
            position:
              "relative",
            width:
              previewStripWidth,
            height:
              previewGeometry
                .pageHeight,
          }}
        >
          {Array.from({
            length:
              previewPageState
                .pageCount,
          }).map(
            (
              _,
              pageIndex
            ) => (
              <div
                key={
                  `preview-page-${pageIndex + 1}`
                }
                data-textbook-preview-page-surface="true"
                data-textbook-preview-page-number={
                  pageIndex + 1
                }
                aria-hidden="true"
                style={{
                  position:
                    "absolute",
                  left:
                    pageIndex *
                    previewGeometry
                      .pagePitch,
                  top: 0,
                  width:
                    previewGeometry
                      .pageWidth,
                  height:
                    previewGeometry
                      .pageHeight,
                  background:
                    "#ffffff",
                  border:
                    "1px solid #d8dee8",
                  borderRadius: 4,
                  boxShadow:
                    "0 18px 45px rgba(47,58,76,0.10)",
                  scrollSnapAlign:
                    "start",
                }}
              />
            )
          )}

          <article
            ref={
              previewFlowRef
            }
            id={chapterAnchorId}
            data-textbook-preview-derived-flow="true"
            style={{
              position:
                "absolute",
              left:
                previewGeometry
                  .horizontalMargin,
              top:
                previewGeometry
                  .verticalMargin,
              width:
                previewGeometry
                  .contentWidth,
              height:
                previewGeometry
                  .contentHeight,
              columnWidth:
                previewGeometry
                  .contentWidth,
              columnGap:
                previewGeometry
                  .columnGap,
              columnFill:
                "auto",
              overflow:
                "visible",
            }}
          >
      {item?.part ? (
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing:
              "0.08em",
            textTransform:
              "uppercase",
            color: "#64748b",
            marginBottom: 5,
          }}
        >
          {isFR
            ? "PARTIE / UNITÉ"
            : "PART / UNIT"}
          {" · "}
          {clean(
            item.part.title
          ) ||
            (
              isFR
                ? "Sans titre"
                : "Untitled"
            )}
        </div>
      ) : null}

      <div
        style={{
          fontSize: 11,
          fontWeight: 900,
          letterSpacing:
            "0.09em",
          textTransform:
            "uppercase",
          color: "#047857",
        }}
      >
        {isFR
          ? `CHAPITRE ${item.chapterNumber}`
          : `CHAPTER ${item.chapterNumber}`}
      </div>

      <h3
        className="ns-section-title ns-textbook-academic-title"
        style={{
          margin:
            "5px 0 14px",
        }}
      >
        {clean(
          item?.title
        ) ||
          (
            isFR
              ? "Chapitre sans titre"
              : "Untitled Chapter"
          )}
      </h3>

      {result?.loading ? (
        <div
          role="status"
          className="ns-card-meta"
        >
          {isFR
            ? "Chargement du chapitre enregistré…"
            : "Loading saved chapter…"}
        </div>
      ) : result?.error ? (
        <div
          role="alert"
          style={{
            padding: 14,
            borderRadius: 12,
            background:
              "#fef2f2",
            color: "#991b1b",
          }}
        >
          <strong>
            {isFR
              ? "Impossible de charger ce chapitre."
              : "Could not load this chapter."}
          </strong>

          <div
            style={{
              marginTop: 5,
            }}
          >
            {result.error}
          </div>
        </div>
      ) : !chapterHasSavedWriting(
          result
        ) ? (
        <div
          className="ns-card ns-card-soft"
          style={{
            padding: 16,
            borderRadius: 14,
          }}
        >
          <strong>
            {isFR
              ? "Aucun texte de chapitre enregistré."
              : "No saved chapter writing yet."}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "6px 0 12px",
            }}
          >
            {isFR
              ? "Revenez à l’Espace Chapitre pour ajouter ou enregistrer le texte."
              : "Return to the Chapter Workspace to add or save the writing."}
          </p>

          {typeof onOpenChapter ===
          "function" ? (
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() =>
                onOpenChapter(
                  item.itemId
                )
              }
            >
              {isFR
                ? "Ouvrir le chapitre"
                : "Open Chapter"}
            </button>
          ) : null}
        </div>
      ) : (
        <div
          data-textbook-preview-flow-content="true"
          style={{
            display: "block",
          }}
        >
          {buildTextbookFigureTextFlowUnits(
            blocks
          ).map(
            (
              unit,
              index
            ) => {
              const block =
                unit.kind ===
                  "figure_text_flow"
                  ? unit.figure
                  : [
                      "rich_manuscript_figure_flow",
                      "cursor_figure_flow",
                    ].includes(
                      unit.kind
                    )
                    ? unit.paragraph
                    : unit.block;

              const sectionItemId =
                clean(
                  block
                    ?.sectionItemId
                );

              const section =
                sectionById.get(
                  sectionItemId
                ) || null;

              const showSection =
                Boolean(
                  sectionItemId &&
                  section &&
                  sectionItemId !==
                    previousSectionId
                );

              const startSectionOnNewPage =
                Boolean(
                  showSection &&
                  startOnNewPageSectionIds
                    .has(
                      sectionItemId
                    )
                );

              previousSectionId =
                sectionItemId;

              return (
                <div
                  key={
                    clean(
                      block?.blockId
                    ) ||
                    `${item.itemId}-${index}`
                  }
                  data-textbook-review-unit-section-id={
                    sectionItemId ||
                    undefined
                  }
                  data-textbook-review-section-start-new-page={
                    startSectionOnNewPage
                      ? "true"
                      : undefined
                  }
                  style={{
                    marginBottom:
                      14,
                    breakBefore:
                      startSectionOnNewPage
                        ? "column"
                        : undefined,
                    breakInside:
                      [
                        "figure",
                        "equation",
                      ].includes(
                        clean(
                          block?.type
                        )
                      )
                        ? "avoid-column"
                        : "auto",
                  }}
                >
                  {showSection ? (
                    <div
                      id={
                        `textbook-review-section-${section.itemId}`
                      }
                      style={{
                        scrollMarginTop:
                          108,
                        borderTop:
                          "1px solid #e2e8f0",
                        paddingTop:
                          14,
                        marginTop: 2,
                      }}
                    >
                      <h4
                        className="ns-card-title ns-textbook-academic-title"
                        style={{
                          margin: 0,
                          fontSize: 18,
                        }}
                      >
                        {clean(
                          section.title
                        ) ||
                          (
                            isFR
                              ? "Section sans titre"
                              : "Untitled Section"
                          )}
                      </h4>
                    </div>
                  ) : null}

                  {[
                    "figure_text_flow",
                    "rich_manuscript_figure_flow",
                    "cursor_figure_flow",
                  ].includes(
                    unit.kind
                  ) ? (
                    <ReviewFigureTextFlowUnit
                      unit={unit}
                      figures={
                        figures
                      }
                      onLoadFigureFile={
                        onLoadFigureFile
                      }
                      isFR={isFR}
                      placedObjectLabels={
                        placedObjectLabels
                      }
                    />
                  ) : clean(
                    block?.type
                  ) ===
                  "figure" ? (
                    <ReviewFigureBlock
                      block={block}
                      figures={
                        figures
                      }
                      onLoadFigureFile={
                        onLoadFigureFile
                      }
                      isFR={isFR}
                      placedLabel={
                        placedObjectLabels[
                          clean(
                            block?.blockId
                          )
                        ] || ""
                      }
                    />
                  ) : (
                    <ReviewTextBlock
                      block={block}
                      isFR={isFR}
                      placedLabel={
                        placedObjectLabels[
                          clean(
                            block?.blockId
                          )
                        ] || ""
                      }
                    />
                  )}
                </div>
              );
            }
          )}
        </div>
      )}

      {result?.figureError ? (
        <div
          role="status"
          className="ns-card-meta"
          style={{
            marginTop: 12,
          }}
        >
          {isFR
            ? "Certaines Figures n’ont pas pu être chargées pour cet aperçu."
            : "Some Figures could not be loaded for this preview."}
        </div>
      ) : null}
          </article>
        </div>
      </div>
    </div>
  );
}

function manuscriptContentHasWriting(
  content
) {
  return safeArray(
    content?.blocks
  ).some(
    (block) =>
      safeArray(
        block?.runs
      ).some(
        (run) =>
          clean(
            run?.text
          )
      )
  );
}


export function ReviewMatterLayoutPreview({
  item,
  formatPlan,
  onPreviewPageCountChange,
  isFR = false,
}) {
  const layoutPreview =
    textbookReviewLayoutPreview(
      formatPlan
    );

  const previewGeometry =
    textbookReviewPreviewPageGeometry(
      layoutPreview
    );

  const previewFlowRef =
    useRef(null);

  const previewViewportRef =
    useRef(null);

  const [
    previewPageState,
    setPreviewPageState,
  ] = useState({
    pageCount: 1,
    pageIndex: 0,
  });

  const hasContent =
    manuscriptContentHasWriting(
      item?.manuscriptContent
    );

  useEffect(() => {
    const flow =
      previewFlowRef.current;

    const viewport =
      previewViewportRef.current;

    if (!flow || !viewport) {
      return undefined;
    }

    let frame = null;
    let timer = null;
    let resizeObserver = null;

    const updatePageCount = () => {
      const overflowWidth =
        Math.max(
          0,
          Number(
            flow.scrollWidth || 0
          ) -
          previewGeometry
            .contentWidth
        );

      const pageCount =
        Math.max(
          1,
          Math.round(
            overflowWidth /
            previewGeometry
              .pagePitch
          ) + 1
        );

      setPreviewPageState(
        (current) => ({
          pageCount,
          pageIndex:
            Math.min(
              current.pageIndex,
              pageCount - 1
            ),
        })
      );

      if (

        typeof onPreviewPageCountChange ===

          "function"

      ) {

        onPreviewPageCountChange(

          clean(item?.itemId),

          pageCount

        );

      }

    };

    const updateCurrentPage = () => {
      const pageIndex =
        Math.max(
          0,
          Math.min(
            previewPageState
              .pageCount - 1,
            Math.round(
              Number(
                viewport.scrollLeft || 0
              ) /
              previewGeometry
                .pagePitch
            )
          )
        );

      setPreviewPageState(
        (current) => (
          pageIndex === current.pageIndex
            ? current
            : {
                ...current,
                pageIndex,
              }
        )
      );
    };

    const scheduleUpdate = () => {
      if (
        typeof window !== "undefined" &&
        typeof window.requestAnimationFrame === "function"
      ) {
        frame =
          window.requestAnimationFrame(
            updatePageCount
          );
      } else {
        updatePageCount();
      }
    };

    flow.addEventListener(
      "load",
      scheduleUpdate,
      true
    );

    viewport.addEventListener(
      "scroll",
      updateCurrentPage,
      { passive: true }
    );

    if (typeof window !== "undefined") {
      window.addEventListener(
        "resize",
        scheduleUpdate
      );

      if (
        typeof window.ResizeObserver === "function"
      ) {
        resizeObserver =
          new window.ResizeObserver(
            scheduleUpdate
          );

        resizeObserver.observe(flow);
      }
    }

    scheduleUpdate();
    timer = setTimeout(updatePageCount, 240);

    return () => {
      flow.removeEventListener(
        "load",
        scheduleUpdate,
        true
      );

      viewport.removeEventListener(
        "scroll",
        updateCurrentPage
      );

      if (typeof window !== "undefined") {
        window.removeEventListener(
          "resize",
          scheduleUpdate
        );

        if (
          frame != null &&
          typeof window.cancelAnimationFrame === "function"
        ) {
          window.cancelAnimationFrame(frame);
        }
      }

      if (timer != null) {
        clearTimeout(timer);
      }

      resizeObserver?.disconnect?.();
    };
  }, [
    item?.itemId,
    item?.manuscriptContent,
    layoutPreview.trimSize,
    previewGeometry.contentWidth,
    previewGeometry.pagePitch,
    previewPageState.pageCount,
    onPreviewPageCountChange,
  ]);

  function goToPreviewPage(requestedIndex) {
    const pageIndex =
      Math.max(
        0,
        Math.min(
          previewPageState.pageCount - 1,
          Number(requestedIndex) || 0
        )
      );

    setPreviewPageState(
      (current) => ({
        ...current,
        pageIndex,
      })
    );

    previewViewportRef.current?.scrollTo?.({
      left:
        pageIndex *
        previewGeometry.pagePitch,
      behavior: "smooth",
    });
  }

  const previewStripWidth =
    (
      previewPageState.pageCount *
      previewGeometry.pageWidth
    ) +
    (
      Math.max(
        0,
        previewPageState.pageCount - 1
      ) *
      previewGeometry.pageGap
    );

  return (
    <div
      data-textbook-book-layout-preview="true"
      data-textbook-whole-book-matter-preview={
        item?.kind || "matter"
      }
      data-textbook-preview-trim={
        layoutPreview.trimSize
      }
      style={{
        padding:
          "18px clamp(10px, 3vw, 28px) 30px",
        borderRadius: 18,
        background: "#eef2f7",
      }}
    >
      <div
        style={{
          maxWidth: layoutPreview.maxWidth,
          margin: "0 auto 10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 12,
          flexWrap: "wrap",
          color: "#475569",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#0f766e",
            }}
          >
            {isFR
              ? "Aperçu de mise en page du livre"
              : "Book Layout Preview"}
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {layoutPreview.ready
              ? (
                  <>
                    {layoutPreview.label}
                    {" · "}
                    {isFR
                      ? "Lecture seule"
                      : "Read-only"}
                  </>
                )
              : (
                  isFR
                    ? "Format du livre non encore sélectionné"
                    : "Book format not selected yet"
                )}
          </div>
        </div>

        <div
          style={{
            maxWidth: 420,
            fontSize: 12,
            lineHeight: 1.45,
            textAlign: "right",
          }}
        >
          {isFR
            ? "Les limites finales des pages seront dérivées pendant la préparation de publication."
            : "Final page boundaries are derived during publication preparation."}
        </div>
      </div>

      <div
        data-textbook-preview-page-navigation="true"
        style={{
          maxWidth: previewGeometry.pageWidth,
          margin: "0 auto 10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={previewPageState.pageIndex <= 0}
          onClick={() =>
            goToPreviewPage(
              previewPageState.pageIndex - 1
            )
          }
        >
          {isFR
            ? "Page d’aperçu précédente"
            : "Previous Preview Page"}
        </button>

        <strong
          data-textbook-preview-page-status="true"
          style={{
            fontSize: 13,
            color: "#334155",
          }}
        >
          {isFR
            ? `Page d’aperçu ${previewPageState.pageIndex + 1} sur ${previewPageState.pageCount}`
            : `Preview page ${previewPageState.pageIndex + 1} of ${previewPageState.pageCount}`}
        </strong>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            previewPageState.pageIndex >=
            previewPageState.pageCount - 1
          }
          onClick={() =>
            goToPreviewPage(
              previewPageState.pageIndex + 1
            )
          }
        >
          {isFR
            ? "Page d’aperçu suivante"
            : "Next Preview Page"}
        </button>
      </div>

      <div
        ref={previewViewportRef}
        data-textbook-preview-page-viewport="true"
        style={{
          width: "100%",
          maxWidth: previewGeometry.pageWidth,
          height: previewGeometry.pageHeight,
          margin: "0 auto",
          overflowX: "auto",
          overflowY: "hidden",
          scrollBehavior: "smooth",
          scrollSnapType: "x mandatory",
        }}
      >
        <div
          style={{
            position: "relative",
            width: previewStripWidth,
            height: previewGeometry.pageHeight,
          }}
        >
          {Array.from({
            length: previewPageState.pageCount,
          }).map((_, pageIndex) => (
            <div
              key={`matter-preview-page-${pageIndex + 1}`}
              data-textbook-preview-page-surface="true"
              data-textbook-preview-page-number={pageIndex + 1}
              aria-hidden="true"
              style={{
                position: "absolute",
                left:
                  pageIndex *
                  previewGeometry.pagePitch,
                top: 0,
                width: previewGeometry.pageWidth,
                height: previewGeometry.pageHeight,
                background: "#ffffff",
                border: "1px solid #d8dee8",
                borderRadius: 4,
                boxShadow:
                  "0 18px 45px rgba(47,58,76,0.10)",
                scrollSnapAlign: "start",
              }}
            />
          ))}

          <article
            ref={previewFlowRef}
            data-textbook-preview-derived-flow="true"
            style={{
              position: "absolute",
              left:
                previewGeometry.horizontalMargin,
              top:
                previewGeometry.verticalMargin,
              width:
                previewGeometry.contentWidth,
              height:
                previewGeometry.contentHeight,
              columnWidth:
                previewGeometry.contentWidth,
              columnGap:
                previewGeometry.columnGap,
              columnFill: "auto",
              overflow: "visible",
            }}
          >
            <h3
              className="ns-section-title ns-textbook-academic-title"
              style={{ margin: "5px 0 16px" }}
            >
              {clean(item?.title) ||
                (
                  isFR
                    ? "Sans titre"
                    : "Untitled"
                )}
            </h3>

            {hasContent ? (
              <ChildrensBookManuscriptReadOnly
                content={item.manuscriptContent}
                lang={isFR ? "fr" : "en"}
                presentation="publication_proof"
              />
            ) : (
              <div
                className="ns-card-meta"
                style={{ color: "#92400e" }}
              >
                {isFR
                  ? "Aucun contenu rédigé."
                  : "No manuscript content has been written yet."}
              </div>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}



function ReviewAutomaticAssemblyMeasurement({
  frontMatter = [],
  chapters = [],
  backMatter = [],
  chapterResults = {},
  formatPlan,
  onLoadFigureFile,
  onPreviewPageCountChange,
  isFR = false,
}) {
  const items =
    useMemo(
      () => [
        ...frontMatter.map(
          (item) => ({
            ...item,
            kind:
              "front_matter",
          })
        ),

        ...chapters.map(
          (item) => ({
            ...item,
            kind:
              "chapter",
          })
        ),

        ...backMatter.map(
          (item) => ({
            ...item,
            kind:
              "back_matter",
          })
        ),
      ],
      [
        frontMatter,
        chapters,
        backMatter,
      ]
    );

  const measurementSignature =
    useMemo(
      () =>
        items
          .map(
            (item) =>
              [
                clean(
                  item?.kind
                ),
                clean(
                  item?.itemId
                ),
                clean(
                  item?.title
                ),
                Number(
                  chapterResults[
                    item?.itemId
                  ]?.chapterDraft
                    ?.revision ||
                  0
                ),
              ].join(":")
          )
          .join("|"),
      [
        items,
        chapterResults,
      ]
    );

  const [
    activeIndex,
    setActiveIndex,
  ] = useState(0);

  const stableTimerRef =
    useRef(null);

  const lastCountRef =
    useRef(0);

  useEffect(() => {
    setActiveIndex(0);
    lastCountRef.current = 0;

    if (
      stableTimerRef.current
    ) {
      clearTimeout(
        stableTimerRef.current
      );

      stableTimerRef.current =
        null;
    }

    return () => {
      if (
        stableTimerRef.current
      ) {
        clearTimeout(
          stableTimerRef.current
        );

        stableTimerRef.current =
          null;
      }
    };
  }, [
    measurementSignature,
  ]);

  const activeItem =
    items[
      activeIndex
    ] ||
    null;

  function acceptMeasuredPageCount(
    itemId,
    pageCount
  ) {
    if (
      !activeItem ||
      clean(itemId) !==
        clean(
          activeItem
            ?.itemId
        ) ||
      !Number.isInteger(
        Number(
          pageCount
        )
      ) ||
      Number(
        pageCount
      ) < 1
    ) {
      return;
    }

    lastCountRef.current =
      Number(
        pageCount
      );

    if (
      stableTimerRef.current
    ) {
      clearTimeout(
        stableTimerRef.current
      );
    }

    stableTimerRef.current =
      setTimeout(
        () => {
          const stableCount =
            Number(
              lastCountRef.current ||
              0
            );

          if (
            stableCount < 1
          ) {
            return;
          }

          onPreviewPageCountChange?.(
            activeItem.kind,
            activeItem.itemId,
            stableCount
          );

          setActiveIndex(
            (current) =>
              Math.min(
                items.length,
                current + 1
              )
          );

          stableTimerRef.current =
            null;
        },
        320
      );
  }

  if (
    !activeItem
  ) {
    return null;
  }

  if (
    activeItem.kind ===
      "chapter"
  ) {
    const result =
      chapterResults[
        activeItem.itemId
      ];

    if (
      !result ||
      result?.loading ||
      clean(
        result?.error
      )
    ) {
      return null;
    }
  }

  return (
    <div
      data-textbook-review-automatic-measurement="true"
      aria-hidden="true"
      style={{
        position:
          "absolute",
        left:
          "-100000px",
        top: 0,
        width:
          1400,
        visibility:
          "hidden",
        pointerEvents:
          "none",
        overflow:
          "hidden",
      }}
    >
      {activeItem.kind ===
        "chapter" ? (
        <ReviewChapter
          item={
            activeItem
          }
          result={
            chapterResults[
              activeItem.itemId
            ]
          }
          formatPlan={
            formatPlan
          }
          onLoadFigureFile={
            onLoadFigureFile
          }
          onPreviewPageCountChange={
            acceptMeasuredPageCount
          }
          isFR={
            isFR
          }
        />
      ) : (
        <ReviewMatterLayoutPreview
          item={{
            ...activeItem,
            kind:
              activeItem.kind ===
                "front_matter"
                ? "front"
                : "back",
          }}
          formatPlan={
            formatPlan
          }
          onPreviewPageCountChange={
            acceptMeasuredPageCount
          }
          isFR={
            isFR
          }
        />
      )}
    </div>
  );
}


function ReviewWholeBookAssembly({
  frontMatter = [],
  chapters = [],
  backMatter = [],
  chapterResults = {},
  chapterStatusResults = {},
  chapterStatusLoading = false,
  formatPlan,
  onOpenChapter,
  onLoadFigureFile,
  onBookItemChange,
  onPreviewPageCountChange,
  projectTitle = "",
  isFR = false,
}) {
  const frontReady =
    frontMatter.filter(
      (item) =>
        manuscriptContentHasWriting(
          item?.manuscriptContent
        )
    ).length;

  const backReady =
    backMatter.filter(
      (item) =>
        manuscriptContentHasWriting(
          item?.manuscriptContent
        )
    ).length;

  const chapterReady =
    chapters.filter(
      (chapter) =>
        chapterHasSavedWriting(
          chapterStatusResults[
            chapter.itemId
          ]
        )
    ).length;

  const wholeBookItems = [
    ...frontMatter.map((item, index) => ({
      ...item,
      kind: "front",
      positionLabel:
        isFR
          ? `Pages liminaires ${index + 1} sur ${frontMatter.length} · ${item.title || "Sans titre"}`
          : `Front Matter ${index + 1} of ${frontMatter.length} · ${item.title || "Untitled"}`,
    })),

    ...chapters.map((chapter) => ({
      ...chapter,
      kind: "chapter",
      positionLabel:
        isFR
          ? `Chapitre ${chapter.chapterNumber} sur ${chapters.length}`
          : `Chapter ${chapter.chapterNumber} of ${chapters.length}`,
    })),

    ...backMatter.map((item, index) => ({
      ...item,
      kind: "back",
      positionLabel:
        isFR
          ? `Élément de fin ${index + 1} sur ${backMatter.length} · ${item.title || "Sans titre"}`
          : `Back Matter ${index + 1} of ${backMatter.length} · ${item.title || "Untitled"}`,
    })),
  ];

  const totalComponents =
    frontMatter.length +
    chapters.length +
    backMatter.length;

  const readyComponents =
    frontReady +
    chapterReady +
    backReady;

  const readinessText =
    chapterStatusLoading
      ? (
          isFR
            ? "Vérification du contenu enregistré…"
            : "Checking saved book content…"
        )
      : (
          readyComponents ===
            totalComponents &&
          totalComponents > 0
            ? (
                isFR
                  ? `Les ${totalComponents} éléments du livre contiennent actuellement du contenu enregistré.`
                  : `All ${totalComponents} book components currently contain saved content.`
              )
            : (
                isFR
                  ? `${readyComponents} sur ${totalComponents} éléments du livre contiennent actuellement du contenu enregistré.`
                  : `${readyComponents} of ${totalComponents} book components currently contain saved content.`
              )
        );

  return (
    <section
      data-textbook-whole-book-assembly="book-preview"
      style={{ marginBottom: 18 }}
    >
      <div
        className="ns-card"
        style={{
          padding: "14px 18px",
          borderRadius: 18,
          border:
            "1px solid rgba(5,150,105,0.18)",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#047857",
          }}
        >
          {isFR
            ? "ASSEMBLAGE ACTUEL DU LIVRE"
            : "CURRENT BOOK ASSEMBLY"}
        </div>

        <h3
          className="ns-card-title ns-textbook-academic-title"
          style={{
            margin: "5px 0 6px",
            fontSize: 24,
          }}
        >
          {isFR
            ? "Révision du livre assemblé"
            : "Whole Book Assembly Preview"}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin: 0,
            maxWidth: 850,
            lineHeight: 1.45,
          }}
        >
          {isFR
            ? "Feuilletez le livre dans son ordre actuel enregistré : pages liminaires, manuscrit principal, puis éléments de fin."
            : "Review the current saved book in reading order: Front Matter, Main Manuscript, then Back Matter."}
        </p>

        <div
          data-textbook-assembly-status="true"
          style={{
            marginTop: 8,
            fontSize: 12,
            fontWeight: 800,
            color: "#166534",
          }}
        >
          {readinessText}
        </div>
      </div>

      <CreativeProjectPreviewShell
        lang={isFR ? "fr" : "en"}
        title={projectTitle}
        items={wholeBookItems}
        previewLabel={
          isFR
            ? "Révision du livre"
            : "Book Review"
        }
        previousLabel={
          isFR
            ? "Section précédente"
            : "Previous Book Section"
        }
        nextLabel={
          isFR
            ? "Section suivante"
            : "Next Book Section"
        }
        description={
          isFR
            ? "Parcourez le livre assemblé tel qu’un lecteur le découvrirait. Cette vue reste privée et en lecture seule."
            : "Read through the assembled book as a reader would. This view remains private and read-only."
        }
        onActiveIndexChange={(
          _nextIndex,
          item
        ) => {
          if (
            typeof onBookItemChange ===
              "function"
          ) {
            onBookItemChange(item);
          }
        }}
        renderItem={(item) => (
          item?.kind === "chapter"
            ? (
                <ReviewChapter
                  item={item}
                  result={
                    chapterResults[
                      item.itemId
                    ] || {
                      loading: true,
                    }
                  }
                  formatPlan={formatPlan}
                  onOpenChapter={onOpenChapter}
                  onLoadFigureFile={onLoadFigureFile}
                  onPreviewPageCountChange={(
                    itemId,
                    pageCount
                  ) =>
                    onPreviewPageCountChange?.(
                      "chapter",
                      itemId,
                      pageCount
                    )
                  }
                  isFR={isFR}
                />
              )
            : (
                <ReviewMatterLayoutPreview
                  item={item}
                  formatPlan={formatPlan}
                  onPreviewPageCountChange={(
                    itemId,
                    pageCount
                  ) =>
                    onPreviewPageCountChange?.(
                      item?.kind === "front"
                        ? "front_matter"
                        : "back_matter",
                      itemId,
                      pageCount
                    )
                  }
                  isFR={isFR}
                />
              )
        )}
      />
    </section>
  );
}





function publicationBlockPreview(block, isFR = false) {
  const type = clean(block?.type) || "content";

  if (type === "figure") {
    return isFR
      ? `Figure · ${clean(block?.figureAssetId) || "élément visuel"}`
      : `Figure · ${clean(block?.figureAssetId) || "visual asset"}`;
  }

  const richBlocks =
    safeArray(
      block?.manuscriptContent?.blocks
    );

  const richText =
    richBlocks
      .map((manuscriptBlock) =>
        safeArray(manuscriptBlock?.runs)
          .map((run) =>
            String(run?.text ?? "")
          )
          .join("")
      )
      .join(" ");

  const raw =
    clean(richText) ||
    clean(block?.text);

  if (!raw) {
    return isFR
      ? `${type} · contenu enregistré`
      : `${type} · saved content`;
  }

  return raw.length > 190
    ? `${raw.slice(0, 190)}…`
    : raw;
}

export default function TextbookBookReview({
  project,
  projectPlan,
  onLoadChapterDraft,
  onLoadFigureAssets,
  onLoadFigureFile,
  onOpenChapter,
  onBackToWriting,
  onLockPublicationManuscript,
  canLockPublicationManuscript = false,
  isFR = false,
}) {
  const projectId =
    clean(
      project?.id
    );

  const frontMatter =
    useMemo(
      () =>
        reviewBookMatter(
          projectPlan
            ?.bookMap,
          "front_matter"
        ),
      [
        projectPlan
          ?.bookMap,
      ]
    );

  const chapters =
    useMemo(
      () =>
        reviewChapters(
          projectPlan
            ?.bookMap
        ),
      [
        projectPlan
          ?.bookMap,
      ]
    );

  const backMatter =
    useMemo(
      () =>
        reviewBookMatter(
          projectPlan
            ?.bookMap,
          "back_matter"
        ),
      [
        projectPlan
          ?.bookMap,
      ]
    );

  const chapterKey =
    chapters
      .map(
        (chapter) =>
          chapter.itemId
      )
      .join("|");

  const [
    results,
    setResults,
  ] = useState({});

  const [
    assemblyResults,
    setAssemblyResults,
  ] = useState({});

  const [
    assemblyLoading,
    setAssemblyLoading,
  ] = useState(false);

  const [
    reviewPreviewPageCounts,
    setReviewPreviewPageCounts,
  ] = useState({});

  const resultsRef =
    useRef({});

  const activeRangeKeyRef =
    useRef("");

  const reviewScopeRef =
    useRef("");

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    selectedChapterId,
    setSelectedChapterId,
  ] = useState("");

  const [
    indexPage,
    setIndexPage,
  ] = useState(0);

  const [
    jumpChapterId,
    setJumpChapterId,
  ] = useState("");

  const [
    publicationLockConfirmed,
    setPublicationLockConfirmed,
  ] = useState(false);

  const [
    publicationLockWorking,
    setPublicationLockWorking,
  ] = useState(false);

  const [
    publicationLockMessage,
    setPublicationLockMessage,
  ] = useState("");

  const [
    publicationLockError,
    setPublicationLockError,
  ] = useState("");


  const publicationAuthority =
    projectPlan
      ?.publicationManuscriptAuthority &&
    typeof projectPlan
      .publicationManuscriptAuthority ===
      "object"
      ? projectPlan
          .publicationManuscriptAuthority
      : {};

  const publicationManuscriptLocked =
    clean(
      publicationAuthority
        ?.status
    ) === "locked" &&
    Boolean(
      clean(
        publicationAuthority
          ?.snapshotId
      )
    );

  const indexPageCount =
    Math.max(
      1,
      Math.ceil(
        chapters.length /
          REVIEW_INDEX_PAGE_SIZE
      )
    );

  useEffect(() => {
    setIndexPage(
      (current) =>
        Math.min(
          current,
          indexPageCount - 1
        )
    );

    if (
      selectedChapterId &&
      !chapters.some(
        (chapter) =>
          chapter.itemId ===
          selectedChapterId
      )
    ) {
      setSelectedChapterId(
        ""
      );
    }

    if (
      jumpChapterId &&
      !chapters.some(
        (chapter) =>
          chapter.itemId ===
          jumpChapterId
      )
    ) {
      setJumpChapterId(
        ""
      );
    }
  }, [
    chapterKey,
    indexPageCount,
    selectedChapterId,
    jumpChapterId,
    chapters,
  ]);

  useEffect(() => {
    let cancelled =
      false;

    if (
      !projectId ||
      !chapters.length ||
      typeof onLoadChapterDraft !==
        "function"
    ) {
      setAssemblyResults(
        {}
      );
      setAssemblyLoading(
        false
      );

      return () => {
        cancelled = true;
      };
    }

    setAssemblyLoading(
      true
    );

    (
      async () => {
        const next = {};
        let cursor = 0;

        async function worker() {
          while (
            !cancelled &&
            cursor <
              chapters.length
          ) {
            const chapter =
              chapters[
                cursor
              ];

            cursor += 1;

            try {
              const payload =
                await onLoadChapterDraft(
                  projectId,
                  chapter.itemId
                );

              let figureAssets =
                [];

              let figureError =
                "";

              if (
                typeof onLoadFigureAssets ===
                  "function"
              ) {
                try {
                  const figurePayload =
                    await onLoadFigureAssets(
                      projectId,
                      chapter.itemId
                    );

                  figureAssets =
                    safeArray(
                      figurePayload
                        ?.figureAssets
                    );
                } catch (
                  figureLoadError
                ) {
                  figureError =
                    clean(
                      figureLoadError
                        ?.message
                    ) ||
                    "Figure load failed.";
                }
              }

              next[
                chapter.itemId
              ] = {
                loading: false,
                error: "",
                chapter:
                  payload
                    ?.chapter ||
                  null,
                chapterDraft:
                  payload
                    ?.chapterDraft ||
                  null,
                figureAssets,
                figureError,
              };
            } catch (
              error
            ) {
              next[
                chapter.itemId
              ] = {
                loading: false,
                error:
                  clean(
                    error
                      ?.message
                  ) ||
                  (
                    isFR
                      ? "Impossible de charger ce chapitre."
                      : "Could not load this chapter."
                  ),
                chapterDraft:
                  null,
                figureAssets: [],
                figureError: "",
              };
            }
          }
        }

        const workerCount =
          Math.max(
            1,
            Math.min(
              4,
              chapters.length
            )
          );

        await Promise.all(
          Array.from(
            {
              length:
                workerCount,
            },
            () =>
              worker()
          )
        );

        if (
          cancelled
        ) {
          return;
        }

        setAssemblyResults(
          next
        );
        setAssemblyLoading(
          false
        );
      }
    )();

    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    chapterKey,
    chapters,
    onLoadChapterDraft,
    onLoadFigureAssets,
    isFR,
  ]);



  const selectedChapterIndex =
    chapters.findIndex(
      (chapter) =>
        chapter.itemId ===
        selectedChapterId
    );

  const indexStart =
    indexPage *
    REVIEW_INDEX_PAGE_SIZE;

  const visibleIndexChapters =
    chapters.slice(
      indexStart,
      indexStart +
        REVIEW_INDEX_PAGE_SIZE
    );

  const indexEnd =
    Math.min(
      indexStart +
        REVIEW_INDEX_PAGE_SIZE,
      chapters.length
    );

  const partCount =
    new Set(
      chapters
        .map(
          (chapter) =>
            clean(
              chapter
                ?.part
                ?.itemId
            )
        )
        .filter(Boolean)
    ).size;

  const sectionCount =
    chapters.reduce(
      (
        total,
        chapter
      ) =>
        total +
        safeArray(
          chapter?.sections
        ).length,
      0
    );

  const activeRangeKey =
    visibleIndexChapters
      .map(
        (chapter) =>
          chapter.itemId
      )
      .join("|");

  activeRangeKeyRef.current =
    activeRangeKey;

  const reviewScopeKey =
    `${projectId}|${chapterKey}`;

  reviewScopeRef.current =
    reviewScopeKey;

  useEffect(() => {
    if (
      !projectId ||
      !visibleIndexChapters.length ||
      typeof onLoadChapterDraft !==
        "function"
    ) {
      resultsRef.current =
        {};

      setResults({});
      setLoading(false);

      return;
    }

    const scopeKey =
      reviewScopeKey;

    const rangeKey =
      activeRangeKey;

    if (
      resultsRef.current
        ?.__reviewScopeKey !==
        scopeKey
    ) {
      resultsRef.current =
        {
          __reviewScopeKey:
            scopeKey,
        };
    }

    const retained = {
      __reviewScopeKey:
        scopeKey,
    };

    visibleIndexChapters
      .forEach(
        (chapter) => {
          const existing =
            resultsRef.current[
              chapter.itemId
            ];

          if (existing) {
            retained[
              chapter.itemId
            ] =
              existing;
          }
        }
      );

    resultsRef.current =
      retained;

    setResults(
      Object.fromEntries(
        Object.entries(
          retained
        ).filter(
          ([key]) =>
            key !==
            "__reviewScopeKey"
        )
      )
    );

    const missing =
      visibleIndexChapters
        .filter(
          (chapter) =>
            !retained[
              chapter.itemId
            ]
        );

    if (
      missing.length === 0
    ) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const withLoading = {
      ...retained,
    };

    missing.forEach(
      (chapter) => {
        withLoading[
          chapter.itemId
        ] = {
          loading: true,
          error: "",
          chapterDraft:
            null,
          figureAssets: [],
          figureError: "",
        };
      }
    );

    resultsRef.current =
      withLoading;

    setResults(
      Object.fromEntries(
        Object.entries(
          withLoading
        ).filter(
          ([key]) =>
            key !==
            "__reviewScopeKey"
        )
      )
    );

    (
      async () => {
        const entries =
          await Promise.all(
            missing.map(
              async (
                chapter
              ) => {
                try {
                  const chapterPayload =
                    await onLoadChapterDraft(
                      projectId,
                      chapter.itemId
                    );

                  let figureAssets =
                    [];

                  let figureError =
                    "";

                  if (
                    typeof onLoadFigureAssets ===
                      "function"
                  ) {
                    try {
                      const figurePayload =
                        await onLoadFigureAssets(
                          projectId,
                          chapter.itemId
                        );

                      figureAssets =
                        safeArray(
                          figurePayload
                            ?.figureAssets
                        );
                    } catch (
                      error
                    ) {
                      figureError =
                        clean(
                          error
                            ?.message
                        ) ||
                        "Figure load failed.";
                    }
                  }

                  return [
                    chapter.itemId,
                    {
                      loading:
                        false,
                      error: "",
                      chapter:
                        chapterPayload
                          ?.chapter ||
                        null,
                      chapterDraft:
                        chapterPayload
                          ?.chapterDraft ||
                        null,
                      figureAssets,
                      figureError,
                    },
                  ];
                } catch (
                  error
                ) {
                  return [
                    chapter.itemId,
                    {
                      loading:
                        false,
                      error:
                        clean(
                          error
                            ?.message
                        ) ||
                        (
                          isFR
                            ? "Impossible de charger ce chapitre."
                            : "Could not load this chapter."
                        ),
                      chapterDraft:
                        null,
                      figureAssets:
                        [],
                      figureError:
                        "",
                    },
                  ];
                }
              }
            )
          );

        if (
          reviewScopeRef.current !==
            scopeKey ||
          activeRangeKeyRef.current !==
            rangeKey
        ) {
          return;
        }

        const next = {
          __reviewScopeKey:
            scopeKey,
        };

        visibleIndexChapters
          .forEach(
            (chapter) => {
              const prior =
                resultsRef.current[
                  chapter.itemId
                ];

              if (prior) {
                next[
                  chapter.itemId
                ] =
                  prior;
              }
            }
          );

        entries.forEach(
          (
            [
              chapterId,
              value,
            ]
          ) => {
            next[
              chapterId
            ] =
              value;
          }
        );

        resultsRef.current =
          next;

        setResults(
          Object.fromEntries(
            Object.entries(
              next
            ).filter(
              ([key]) =>
                key !==
                "__reviewScopeKey"
            )
          )
        );

        setLoading(false);
      }
    )();
  }, [
    projectId,
    chapterKey,
    activeRangeKey,
    reviewScopeKey,
    onLoadChapterDraft,
    onLoadFigureAssets,
    isFR,
  ]);

  const rangeSavedCount =
    visibleIndexChapters
      .filter(
        (chapter) =>
          chapterHasSavedWriting(
            results[
              chapter.itemId
            ]
          )
      ).length;

  const rangeFailedCount =
    visibleIndexChapters
      .filter(
        (chapter) =>
          clean(
            results[
              chapter.itemId
            ]?.error
          )
      ).length;

  const rangePending =
    loading ||
    visibleIndexChapters
      .some(
        (chapter) =>
          !results[
            chapter.itemId
          ] ||
          results[
            chapter.itemId
          ]?.loading
      );

  const publicationSourceSummary =
    chapters.reduce(
      (
        summary,
        chapter
      ) => {
        const blocks =
          safeArray(
            assemblyResults[
              chapter.itemId
            ]?.chapterDraft?.blocks
          );

        summary.blockCount +=
          blocks.length;

        summary.figureCount +=
          blocks.filter(
            (block) =>
              clean(
                block?.type
              ) === "figure"
          ).length;

        return summary;
      },
      {
        chapterCount:
          chapters.length,
        blockCount:
          0,
        figureCount:
          0,
      }
    );

  const reviewMeasurementKeys = [
    ...frontMatter.map(
      (item) =>
        `front_matter:${item.itemId}`
    ),
    ...chapters.map(
      (chapter) =>
        `chapter:${chapter.itemId}`
    ),
    ...backMatter.map(
      (item) =>
        `back_matter:${item.itemId}`
    ),
  ];

  const reviewMeasurementRequiredCount =
    reviewMeasurementKeys.length;

  const reviewMeasurementCompleteCount =
    reviewMeasurementKeys.filter(
      (key) =>
        Number(
          reviewPreviewPageCounts[
            key
          ] ||
          0
        ) >= 1
    ).length;

  const reviewMeasurementComplete =
    reviewMeasurementRequiredCount > 0 &&
    reviewMeasurementCompleteCount ===
      reviewMeasurementRequiredCount;

  const publicationAssemblyReady =
    chapters.length > 0;

  const allChaptersReadyForPublicationLock =
    chapters.length > 0 &&
    !assemblyLoading &&
    chapters.every(
      (chapter) => {
        const result =
          assemblyResults[
            chapter.itemId
          ];

        return (
          chapterHasSavedWriting(
            result
          ) &&
          !clean(
            result?.error
          )
        );
      }
    );

  async function lockPublicationManuscript() {
    const reviewAssembly = {
      schemaVersion:
        "textbook-review-assembly-v1",

      trimSize:
        clean(
          projectPlan
            ?.formatPlan
            ?.trimSize
        ),

      frontMatter:
        frontMatter.map(
          (item) => ({
            itemId:
              item.itemId,
            title:
              clean(
                item.title
              ),
            pageCount:
              Number(
                reviewPreviewPageCounts[
                  `front_matter:${item.itemId}`
                ] || 0
              ),
          })
        ),

      chapters:
        chapters.map(
          (chapter) => ({
            itemId:
              chapter.itemId,
            title:
              clean(
                chapter.title
              ),
            revision:
              Number(
                assemblyResults[
                  chapter.itemId
                ]?.chapterDraft
                  ?.revision ||
                0
              ),
            pageCount:
              Number(
                reviewPreviewPageCounts[
                  `chapter:${chapter.itemId}`
                ] || 0
              ),
          })
        ),

      backMatter:
        backMatter.map(
          (item) => ({
            itemId:
              item.itemId,
            title:
              clean(
                item.title
              ),
            pageCount:
              Number(
                reviewPreviewPageCounts[
                  `back_matter:${item.itemId}`
                ] || 0
              ),
          })
        ),
    };

    reviewAssembly
      .totalPreviewPageCount =
      [
        ...reviewAssembly.frontMatter,
        ...reviewAssembly.chapters,
        ...reviewAssembly.backMatter,
      ].reduce(
        (total, item) =>
          total +
          Number(
            item.pageCount ||
            0
          ),
        0
      );

    const reviewAssemblyReady =
      Boolean(
        reviewAssembly.trimSize
      ) &&
      reviewAssembly.frontMatter.every(
        (item) =>
          item.pageCount >= 1
      ) &&
      reviewAssembly.chapters.every(
        (item) =>
          item.pageCount >= 1 &&
          item.revision >= 1
      ) &&
      reviewAssembly.backMatter.every(
        (item) =>
          item.pageCount >= 1
      );

    const revision =
      Number(
        projectPlan?.revision ||
        0
      );

    if (
      !canLockPublicationManuscript ||
      typeof onLockPublicationManuscript !==
        "function"
    ) {
      return;
    }

    if (
      !allChaptersReadyForPublicationLock ||
      !publicationAssemblyReady ||
      !reviewMeasurementComplete ||
      !reviewAssemblyReady
    ) {
      setPublicationLockError(
        !allChaptersReadyForPublicationLock
          ? (
              isFR
                ? "Tous les chapitres doivent avoir un Canon enregistré et lisible avant le verrouillage."
                : "Every Chapter must have saved, readable Canon before the publication manuscript can be locked."
            )
          : (
              isFR
                ? "NextScenes mesure encore automatiquement les pages de Révision du livre. Attendez la fin de la mesure avant de verrouiller."
                : "NextScenes is still measuring the Review pages automatically. Wait for Review measurement to finish before locking."
            )
      );
      return;
    }

    if (
      !publicationLockConfirmed
    ) {
      setPublicationLockError(
        isFR
          ? "Confirmez d’abord que le livre enregistré affiché est bien la version destinée à l’impression."
          : "First confirm that the saved book shown here is the version intended for print."
      );
      return;
    }

    if (
      !Number.isInteger(
        revision
      ) ||
      revision < 1
    ) {
      setPublicationLockError(
        isFR
          ? "Rechargez le livre avant de verrouiller le manuscrit de publication."
          : "Reload the book before locking the publication manuscript."
      );
      return;
    }

    try {
      setPublicationLockWorking(
        true
      );
      setPublicationLockError("");
      setPublicationLockMessage("");

      await onLockPublicationManuscript(
        revision,
        reviewAssembly
      );

      setPublicationLockConfirmed(
        false
      );

      setPublicationLockMessage(
        isFR
          ? "Manuscrit de publication verrouillé. La production utilisera exactement cette version enregistrée."
          : "Publication manuscript locked. Production will use exactly this immutable reviewed publication source."
      );
    } catch (error) {
      setPublicationLockError(
        clean(
          error?.message
        ) ||
        (
          isFR
            ? "Impossible de verrouiller le manuscrit de publication."
            : "Could not lock the publication manuscript."
        )
      );
    } finally {
      setPublicationLockWorking(
        false
      );
    }
  }

  const previewItems =
    chapters.map(
      (chapter) => ({
        ...chapter,
        positionLabel:
          isFR
            ? `Chapitre ${chapter.chapterNumber} sur ${chapters.length}`
            : `Chapter ${chapter.chapterNumber} of ${chapters.length}`,
      })
    );

  const indexGroups =
    visibleIndexChapters.reduce(
      (
        groups,
        chapter
      ) => {
        const groupKey =
          clean(
            chapter
              ?.part
              ?.itemId
          ) ||
          "__ungrouped__";

        const previous =
          groups[
            groups.length - 1
          ];

        if (
          previous &&
          previous.key ===
            groupKey
        ) {
          previous.chapters.push(
            chapter
          );

          return groups;
        }

        groups.push({
          key:
            groupKey,
          part:
            chapter.part ||
            null,
          chapters: [
            chapter,
          ],
        });

        return groups;
      },
      []
    );

  function reviewChapter(
    chapterId
  ) {
    const id =
      clean(
        chapterId
      );

    if (!id) {
      return;
    }

    const nextIndex =
      chapters.findIndex(
        (chapter) =>
          chapter.itemId ===
          id
      );

    if (
      nextIndex >= 0
    ) {
      setIndexPage(
        Math.floor(
          nextIndex /
            REVIEW_INDEX_PAGE_SIZE
        )
      );
    }

    setJumpChapterId(
      id
    );

    setSelectedChapterId(
      id
    );

    if (
      typeof window !==
        "undefined"
    ) {
      window.scrollTo?.({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }
  }

  function syncPreviewChapter(
    nextIndex,
    item
  ) {
    const id =
      clean(
        item?.itemId
      );

    if (!id) {
      return;
    }

    setSelectedChapterId(
      id
    );

    setJumpChapterId(
      id
    );

    setIndexPage(
      Math.floor(
        nextIndex /
          REVIEW_INDEX_PAGE_SIZE
      )
    );
  }

  function jumpToChapterRange(
    chapterId
  ) {
    const id =
      clean(
        chapterId
      );

    setJumpChapterId(
      id
    );

    if (!id) {
      return;
    }

    const nextIndex =
      chapters.findIndex(
        (chapter) =>
          chapter.itemId ===
          id
      );

    if (
      nextIndex < 0
    ) {
      return;
    }

    setIndexPage(
      Math.floor(
        nextIndex /
          REVIEW_INDEX_PAGE_SIZE
      )
    );
  }

  const reviewHeader = (
    <div
      className="ns-card"
      style={{
        padding: 20,
        borderRadius: 18,
        border:
          "1.5px solid rgba(5,150,105,0.24)",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 900,
          letterSpacing:
            "0.1em",
          textTransform:
            "uppercase",
          color: "#047857",
        }}
      >
        NEXTSCENES BOOK BLUEPRINT ·
        {" "}
        {isFR
          ? "RÉVISER VOTRE LIVRE"
          : "REVIEW YOUR BOOK"}
      </div>

      <h3
        className="ns-card-title ns-textbook-academic-title"
        style={{
          margin:
            "6px 0 7px",
          fontSize: 25,
        }}
      >
        {clean(
          project?.title
        ) ||
          (
            isFR
              ? "Manuel sans titre"
              : "Untitled Textbook"
          )}
      </h3>

      <p
        className="ns-card-meta"
        style={{
          margin: 0,
          maxWidth: 800,
          lineHeight: 1.6,
        }}
      >
        {isFR
          ? "Révisez le manuel chapitre par chapitre, avec accès direct aux sections. Cette vue reste privée et en lecture seule : elle n’approuve, ne publie et n’exporte rien. Les pages physiques imprimées seront préparées plus tard pendant la préparation de publication. L’aperçu du chapitre utilise le format de livre sélectionné pour ses proportions ; les limites finales des pages seront alors dérivées."
          : "Review the current book in reading order: Front Matter, Main Manuscript, then Back Matter. This view remains private and read-only: it does not approve, publish, or export anything. Open any Chapter for its full manuscript preview; final physical page boundaries are derived later during publication preparation."}
      </p>

      <div
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10,
        }}
      >
        {[
          {
            label:
              isFR
                ? "Pages liminaires"
                : "Front Matter",
            value:
              frontMatter.length,
          },
          {
            label:
              isFR
                ? "Chapitres"
                : "Chapters",
            value:
              chapters.length,
          },
          {
            label:
              isFR
                ? "Parties / Unités"
                : "Parts / Units",
            value:
              partCount,
          },
          {
            label:
              isFR
                ? "Sections"
                : "Sections",
            value:
              sectionCount,
          },
          {
            label:
              isFR
                ? "Éléments de fin"
                : "Back Matter",
            value:
              backMatter.length,
          },
          {
            label:
              isFR
                ? "Enregistrés dans cette plage"
                : "Saved in this range",
            value:
              rangePending
                ? "…"
                : `${rangeSavedCount}/${visibleIndexChapters.length}`,
          },
        ].map(
          (metric) => (
            <div
              key={
                metric.label
              }
              className="ns-card ns-card-soft"
              style={{
                padding:
                  "11px 12px",
                borderRadius:
                  12,
              }}
            >
              <div
                className="ns-card-meta"
                style={{
                  fontSize: 11,
                  fontWeight:
                    800,
                }}
              >
                {metric.label}
              </div>

              <div
                style={{
                  marginTop: 3,
                  fontSize: 21,
                  fontWeight:
                    900,
                  color:
                    "#1e293b",
                }}
              >
                {metric.value}
              </div>
            </div>
          )
        )}
      </div>

      {rangeFailedCount > 0 ? (
        <div
          role="status"
          className="ns-card-meta"
          style={{
            marginTop: 9,
            color: "#991b1b",
          }}
        >
          {isFR
            ? `${rangeFailedCount} chapitre(s) de cette plage n’ont pas pu être chargés.`
            : `${rangeFailedCount} chapter(s) in this range could not be loaded.`}
        </div>
      ) : null}
    </div>
  );

  if (
    selectedChapterIndex >= 0
  ) {
    return (
      <section>
        {reviewHeader}

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 14,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              setSelectedChapterId(
                ""
              )
            }
          >
            {isFR
              ? "Retour à la liste des chapitres"
              : "Back to Chapter List"}
          </button>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={
              onBackToWriting
            }
          >
            {isFR
              ? "Retour à Rédiger les chapitres"
              : "Back to Write Chapters"}
          </button>
        </div>

        <CreativeProjectPreviewShell
          key={
            selectedChapterId
          }
          lang={
            isFR
              ? "fr"
              : "en"
          }
          title={
            clean(
              project?.title
            )
          }
          items={
            previewItems
          }
          initialActiveIndex={
            selectedChapterIndex
          }
          onActiveIndexChange={
            syncPreviewChapter
          }
          renderItem={(
            item
          ) => (
            <ReviewChapter
              item={item}
              result={
                results[
                  item.itemId
                ] || {
                  loading,
                }
              }
              formatPlan={
                projectPlan
                  ?.formatPlan
              }
              onOpenChapter={
                onOpenChapter
              }
              onLoadFigureFile={
                onLoadFigureFile
              }
              isFR={isFR}
            />
          )}
          onReturn={() =>
            setSelectedChapterId(
              ""
            )
          }
          returnLabel={
            isFR
              ? "Retour à la liste des chapitres"
              : "Back to Chapter List"
          }
        />
      </section>
    );
  }

  return (
    <section>
      {reviewHeader}

      <div
        className="ns-card"
        data-textbook-publication-manuscript-lock="true"
        style={{
          padding: 18,
          borderRadius: 18,
          border:
            publicationManuscriptLocked
              ? "1.5px solid #86efac"
              : "1.5px solid #fbbf24",
          background:
            publicationManuscriptLocked
              ? "#f0fdf4"
              : "#fffbeb",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              maxWidth: 760,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.08em",
                textTransform:
                  "uppercase",
                color:
                  publicationManuscriptLocked
                    ? "#047857"
                    : "#92400e",
              }}
            >
              {isFR
                ? "AUTORITÉ DU MANUSCRIT DE PUBLICATION"
                : "PUBLICATION MANUSCRIPT AUTHORITY"}
            </div>

            <h3
              className="ns-card-title ns-textbook-academic-title"
              style={{
                margin:
                  "5px 0 7px",
              }}
            >
              {publicationManuscriptLocked
                ? (
                    isFR
                      ? "Manuscrit verrouillé pour la production"
                      : "Publication manuscript locked"
                  )
                : (
                    isFR
                      ? "Révision requise avant la production"
                      : "Review required before production"
                  )}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.55,
              }}
            >
              {publicationManuscriptLocked
                ? (
                    isFR
                      ? "NextScenes utilisera la version enregistrée et immuable que vous avez verrouillée. Les modifications de travail ultérieures ne remplacent pas silencieusement ce manuscrit de publication."
                      : "NextScenes will use the exact immutable saved version you locked. Later working edits do not silently replace this publication manuscript."
                  )
                : (
                    isFR
                      ? "Lisez le livre assemblé ci-dessous. Ne verrouillez que lorsque le Canon actuellement enregistré est exactement la version que vous souhaitez envoyer à la production."
                      : "Read the assembled book below. Lock only when the currently saved Canon is exactly the version you intend to send to production."
                  )}
            </p>
          </div>

          <div
            style={{
              borderRadius: 999,
              padding:
                "7px 12px",
              fontSize: 12,
              fontWeight: 900,
              background:
                publicationManuscriptLocked
                  ? "#dcfce7"
                  : "#fef3c7",
              color:
                publicationManuscriptLocked
                  ? "#065f46"
                  : "#92400e",
            }}
          >
            {publicationManuscriptLocked
              ? (
                  isFR
                    ? "VERROUILLÉ"
                    : "LOCKED"
                )
              : (
                  isFR
                    ? "RÉVISION REQUISE"
                    : "REVIEW REQUIRED"
                )}
          </div>
        </div>

        <div
          style={{
            marginTop: 14,
            display: "grid",
            gap: 12,
          }}
        >
          <div
            className="ns-card-meta"
            style={{
              fontWeight: 700,
              color:
                allChaptersReadyForPublicationLock
                  ? "#166534"
                  : "#92400e",
            }}
          >
            {assemblyLoading
              ? (
                  isFR
                    ? "Vérification des Canons enregistrés…"
                    : "Checking saved Chapter Canon…"
                )
              : allChaptersReadyForPublicationLock
                ? (
                    isFR
                      ? "Tous les chapitres ont un Canon enregistré et lisible. NextScenes prépare automatiquement la source de publication à partir de la Révision du livre."
                      : "NextScenes will prepare the publication source automatically from the reviewed saved Canon. You only need to confirm the book is ready for production."
                  )
                : (
                    isFR
                      ? "Le verrouillage reste désactivé jusqu’à ce que tous les chapitres aient un Canon enregistré et lisible."
                      : "Locking remains disabled until every Chapter has saved, readable Canon."
                  )}
          </div>

          {!assemblyLoading &&
          allChaptersReadyForPublicationLock ? (
            <div
              data-textbook-publication-source-v3="true"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 9,
              }}
            >
              {[
                {
                  label:
                    isFR
                      ? "Chapitres"
                      : "Chapters",
                  value:
                    publicationSourceSummary.chapterCount,
                },
                {
                  label:
                    isFR
                      ? "Blocs Canon"
                      : "Canon blocks",
                  value:
                    publicationSourceSummary.blockCount,
                },
                {
                  label:
                    isFR
                      ? "Figures"
                      : "Figures",
                  value:
                    publicationSourceSummary.figureCount,
                },
                {
                  label:
                    isFR
                      ? "État"
                      : "Status",
                  value:
                    !reviewMeasurementComplete
                      ? (
                          isFR
                            ? `Mesure ${reviewMeasurementCompleteCount}/${reviewMeasurementRequiredCount}`
                            : `Measuring review ${reviewMeasurementCompleteCount}/${reviewMeasurementRequiredCount}`
                        )
                      : publicationManuscriptLocked
                        ? (
                            isFR
                              ? "Révision actuelle prête"
                              : "Current review ready"
                          )
                        : (
                            isFR
                              ? "Prêt à verrouiller"
                              : "Ready to lock"
                          ),
                },
              ].map(
                (metric) => (
                  <div
                    key={metric.label}
                    className="ns-card ns-card-soft"
                    style={{
                      padding:
                        "10px 12px",
                      borderRadius:
                        12,
                    }}
                  >
                    <div
                      className="ns-card-meta"
                      style={{
                        fontSize:
                          11,
                        fontWeight:
                          800,
                      }}
                    >
                      {metric.label}
                    </div>

                    <div
                      style={{
                        marginTop:
                          3,
                        fontSize:
                          typeof metric.value ===
                            "number"
                            ? 20
                            : 13,
                        fontWeight:
                          900,
                        color:
                          "#166534",
                      }}
                    >
                      {metric.value}
                    </div>
                  </div>
                )
              )}
            </div>
          ) : null}

          <label
            style={{
              display: "flex",
              gap: 9,
              alignItems:
                "flex-start",
              maxWidth: 900,
              fontWeight: 700,
              lineHeight: 1.45,
            }}
          >
            <input
              type="checkbox"
              checked={
                publicationLockConfirmed
              }
              disabled={
                !canLockPublicationManuscript ||
                publicationLockWorking ||
                !allChaptersReadyForPublicationLock ||
                !publicationAssemblyReady ||
                !reviewMeasurementComplete
              }
              onChange={(event) => {
                setPublicationLockConfirmed(
                  event.target.checked
                );
                setPublicationLockError("");
              }}
            />

            <span>
              {isFR
                ? "Je confirme avoir révisé l’assemblage de publication sélectionné ci-dessus et qu’il correspond exactement à la version destinée à la production."
                : "I confirm that I have reviewed the selected publication assembly above and that it is exactly the version intended for production."}
            </span>
          </label>

          <div>
            <button
              type="button"
              className="ns-button ns-button-primary"
              data-textbook-lock-publication-manuscript="true"
              disabled={
                !canLockPublicationManuscript ||
                publicationLockWorking ||
                !allChaptersReadyForPublicationLock ||
                !publicationAssemblyReady ||
                !reviewMeasurementComplete ||
                !publicationLockConfirmed
              }
              onClick={
                lockPublicationManuscript
              }
            >
              {publicationLockWorking
                ? (
                    isFR
                      ? "Verrouillage…"
                      : "Locking…"
                  )
                : publicationManuscriptLocked
                  ? (
                      isFR
                        ? "Remplacer le manuscrit de publication verrouillé"
                        : "Replace Locked Publication Manuscript"
                    )
                  : (
                      isFR
                        ? "Verrouiller le manuscrit de publication"
                        : "Lock Publication Manuscript"
                    )}
            </button>
          </div>
        </div>

        {publicationLockError ? (
          <div
            style={{
              marginTop: 12,
              color: "#991b1b",
              fontWeight: 700,
            }}
          >
            {publicationLockError}
          </div>
        ) : null}

        {publicationLockMessage ? (
          <div
            style={{
              marginTop: 12,
              color: "#166534",
              fontWeight: 700,
            }}
          >
            {publicationLockMessage}
          </div>
        ) : null}
      </div>

      <ReviewWholeBookAssembly
        frontMatter={
          frontMatter
        }
        chapters={
          chapters
        }
        backMatter={
          backMatter
        }
        chapterResults={
          results
        }
        chapterStatusResults={
          assemblyResults
        }
        chapterStatusLoading={
          assemblyLoading
        }
        formatPlan={
          projectPlan?.formatPlan
        }
        onOpenChapter={
          onOpenChapter
        }
        onLoadFigureFile={
          onLoadFigureFile
        }
        onPreviewPageCountChange={(
          kind,
          itemId,
          pageCount
        ) => {
          const key =
            `${clean(kind)}:${clean(itemId)}`;

          if (
            !clean(kind) ||
            !clean(itemId) ||
            !Number.isInteger(
              Number(pageCount)
            ) ||
            Number(pageCount) < 1
          ) {
            return;
          }

          setReviewPreviewPageCounts(
            (current) =>
              current[key] ===
                Number(pageCount)
                ? current
                : {
                    ...current,
                    [key]:
                      Number(pageCount),
                  }
          );
        }}
        onBookItemChange={(
          item
        ) => {
          if (
            item?.kind !==
              "chapter"
          ) {
            return;
          }

          const chapterIndex =
            chapters.findIndex(
              (chapter) =>
                chapter.itemId ===
                item.itemId
            );

          if (chapterIndex >= 0) {
            setIndexPage(
              Math.floor(
                chapterIndex /
                  REVIEW_INDEX_PAGE_SIZE
              )
            );
          }
        }}
        projectTitle={
          clean(project?.title)
        }
        isFR={isFR}
      />

      <ReviewAutomaticAssemblyMeasurement
        frontMatter={
          frontMatter
        }
        chapters={
          chapters
        }
        backMatter={
          backMatter
        }
        chapterResults={
          assemblyResults
        }
        formatPlan={
          projectPlan?.formatPlan
        }
        onLoadFigureFile={
          onLoadFigureFile
        }
        onPreviewPageCountChange={(
          kind,
          itemId,
          pageCount
        ) => {
          const key =
            `${clean(kind)}:${clean(itemId)}`;

          if (
            !clean(kind) ||
            !clean(itemId) ||
            !Number.isInteger(
              Number(pageCount)
            ) ||
            Number(pageCount) < 1
          ) {
            return;
          }

          setReviewPreviewPageCounts(
            (current) =>
              current[key] ===
                Number(pageCount)
                ? current
                : {
                    ...current,
                    [key]:
                      Number(pageCount),
                  }
          );
        }}
        isFR={
          isFR
        }
      />

      <div
        className="ns-card"
        data-textbook-review-main-manuscript="true"
        style={{
          padding: 18,
          borderRadius: 18,
          border:
            "1px solid rgba(5,150,105,0.18)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 14,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div>
            <h3
              className="ns-card-title ns-textbook-academic-title"
              style={{
                margin:
                  "0 0 5px",
              }}
            >
              {isFR
                ? "Manuscrit principal"
                : "Main Manuscript"}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                maxWidth: 720,
              }}
            >
              {isFR
                ? "Choisissez un chapitre pour lire le manuscrit principal. L’index reste limité pour que les grands manuels restent faciles à parcourir."
                : "Choose a Chapter to read the main manuscript. The index stays bounded so large textbooks remain easy to navigate."}
            </p>
          </div>

          {chapters.length > 0 ? (
            <button
              className="ns-button ns-button-primary"
              type="button"
              onClick={() =>
                reviewChapter(
                  chapters[0]
                    .itemId
                )
              }
            >
              {isFR
                ? "Lire depuis le chapitre 1"
                : "Read from Chapter 1"}
            </button>
          ) : null}
        </div>

        {chapters.length === 0 ? (
          <div
            className="ns-card ns-card-soft"
            style={{
              padding: 16,
              borderRadius: 14,
            }}
          >
            <strong>
              {isFR
                ? "Aucun chapitre dans la Structure du livre."
                : "No Chapters in Book Structure yet."}
            </strong>
          </div>
        ) : (
          <>
            <div
              className="ns-card ns-card-soft"
              style={{
                padding: 12,
                borderRadius: 14,
                display: "grid",
                gridTemplateColumns:
                  "auto minmax(240px, 1fr) auto",
                alignItems:
                  "end",
                gap: 12,
                marginBottom:
                  16,
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  indexPage <= 0
                }
                onClick={() => {
                  setJumpChapterId(
                    ""
                  );

                  setIndexPage(
                    (current) =>
                      Math.max(
                        0,
                        current - 1
                      )
                  );
                }}
              >
                {isFR
                  ? "Plage précédente"
                  : "Previous Range"}
              </button>

              <div>
                <strong>
                  {isFR
                    ? `Chapitres ${indexStart + 1}–${indexEnd} sur ${chapters.length}`
                    : `Chapters ${indexStart + 1}–${indexEnd} of ${chapters.length}`}
                </strong>

                <label
                  style={{
                    display:
                      "grid",
                    gap: 5,
                    marginTop: 8,
                    fontWeight:
                      800,
                  }}
                >
                  <span>
                    {isFR
                      ? "Aller au chapitre"
                      : "Jump to Chapter"}
                  </span>

                  <select
                    value={
                      jumpChapterId
                    }
                    onChange={(
                      event
                    ) =>
                      jumpToChapterRange(
                        event
                          .target
                          .value
                      )
                    }
                    style={{
                      width: "100%",
                      minHeight: 40,
                      padding:
                        "7px 9px",
                      borderRadius:
                        10,
                      border:
                        "1px solid #cbd5e1",
                      background:
                        "#ffffff",
                      color:
                        "#0f172a",
                      fontWeight:
                        700,
                    }}
                  >
                    <option value="">
                      {isFR
                        ? "Choisir un chapitre"
                        : "Choose a Chapter"}
                    </option>

                    {chapters.map(
                      (chapter) => (
                        <option
                          key={
                            chapter.itemId
                          }
                          value={
                            chapter.itemId
                          }
                        >
                          {isFR
                            ? `Chapitre ${chapter.chapterNumber}`
                            : `Chapter ${chapter.chapterNumber}`}
                          {" · "}
                          {clean(
                            chapter.title
                          ) ||
                            (
                              isFR
                                ? "Sans titre"
                                : "Untitled"
                            )}
                        </option>
                      )
                    )}
                  </select>
                </label>
              </div>

              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  indexPage >=
                  indexPageCount - 1
                }
                onClick={() => {
                  setJumpChapterId(
                    ""
                  );

                  setIndexPage(
                    (current) =>
                      Math.min(
                        indexPageCount - 1,
                        current + 1
                      )
                  );
                }}
              >
                {isFR
                  ? "Plage suivante"
                  : "Next Range"}
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gap: 18,
              }}
            >
              {indexGroups.map(
                (
                  group,
                  groupIndex
                ) => (
                  <section
                    key={
                      `${group.key}-${groupIndex}`
                    }
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight:
                          900,
                        letterSpacing:
                          "0.08em",
                        textTransform:
                          "uppercase",
                        color:
                          "#64748b",
                        marginBottom:
                          9,
                      }}
                    >
                      {group.part
                        ? (
                            <>
                              {isFR
                                ? "PARTIE / UNITÉ"
                                : "PART / UNIT"}
                              {" · "}
                              {clean(
                                group
                                  .part
                                  .title
                              ) ||
                                (
                                  isFR
                                    ? "Sans titre"
                                    : "Untitled"
                                )}
                            </>
                          )
                        : (
                            isFR
                              ? "CHAPITRES"
                              : "CHAPTERS"
                          )}
                    </div>

                    <div
                      style={{
                        display:
                          "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(250px, 1fr))",
                        gap: 12,
                      }}
                    >
                      {group
                        .chapters
                        .map(
                          (
                            chapter
                          ) => {
                            const result =
                              results[
                                chapter
                                  .itemId
                              ] || {};

                            const saved =
                              chapterHasSavedWriting(
                                result
                              );

                            const sectionCount =
                              safeArray(
                                chapter
                                  .sections
                              ).length;

                            const figureCount =
                              safeArray(
                                result
                                  .figureAssets
                              ).length;

                            const blockCount =
                              safeArray(
                                result
                                  ?.chapterDraft
                                  ?.blocks
                              ).length;

                            const hasError =
                              Boolean(
                                clean(
                                  result
                                    ?.error
                                )
                              );

                            return (
                              <article
                                key={
                                  chapter.itemId
                                }
                                className="ns-card ns-card-soft"
                                style={{
                                  padding: 15,
                                  borderRadius:
                                    14,
                                  minHeight:
                                    210,
                                  display:
                                    "flex",
                                  flexDirection:
                                    "column",
                                  gap: 9,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize:
                                      11,
                                    fontWeight:
                                      900,
                                    letterSpacing:
                                      "0.07em",
                                    textTransform:
                                      "uppercase",
                                    color:
                                      "#047857",
                                  }}
                                >
                                  {isFR
                                    ? `CHAPITRE ${chapter.chapterNumber}`
                                    : `CHAPTER ${chapter.chapterNumber}`}
                                </div>

                                <h4
                                  className="ns-card-title ns-textbook-academic-title"
                                  style={{
                                    margin: 0,
                                    fontSize:
                                      18,
                                  }}
                                >
                                  {clean(
                                    chapter
                                      .title
                                  ) ||
                                    (
                                      isFR
                                        ? "Chapitre sans titre"
                                        : "Untitled Chapter"
                                    )}
                                </h4>

                                <div
                                  className="ns-card-meta"
                                  style={{
                                    lineHeight:
                                      1.5,
                                  }}
                                >
                                  {sectionCount}
                                  {" "}
                                  {isFR
                                    ? "section(s)"
                                    : "Section(s)"}
                                  {" · "}
                                  {figureCount}
                                  {" "}
                                  {isFR
                                    ? "Figure(s)"
                                    : "Figure(s)"}
                                  {" · "}
                                  {blockCount}
                                  {" "}
                                  {isFR
                                    ? "bloc(s)"
                                    : "content block(s)"}
                                </div>

                                <div
                                  style={{
                                    fontWeight:
                                      800,
                                    color:
                                      hasError
                                        ? "#991b1b"
                                        : (
                                            saved
                                              ? "#047857"
                                              : "#92400e"
                                          ),
                                  }}
                                >
                                  {result
                                    ?.loading
                                    ? (
                                        isFR
                                          ? "Chargement…"
                                          : "Loading…"
                                      )
                                    : hasError
                                      ? (
                                          isFR
                                            ? "À vérifier"
                                            : "Needs attention"
                                        )
                                      : saved
                                        ? (
                                            isFR
                                              ? "Prêt à réviser"
                                              : "Ready to review"
                                          )
                                        : (
                                            isFR
                                              ? "Aucun texte enregistré"
                                              : "No saved writing"
                                          )}
                                </div>

                                <div
                                  style={{
                                    marginTop:
                                      "auto",
                                  }}
                                >
                                  {saved ? (
                                    <button
                                      className="ns-button ns-button-primary"
                                      type="button"
                                      onClick={() =>
                                        reviewChapter(
                                          chapter.itemId
                                        )
                                      }
                                    >
                                      {isFR
                                        ? "Réviser le chapitre"
                                        : "Review Chapter"}
                                    </button>
                                  ) : typeof onOpenChapter ===
                                    "function" ? (
                                    <button
                                      className="ns-button ns-button-outline"
                                      type="button"
                                      disabled={
                                        result
                                          ?.loading
                                      }
                                      onClick={() =>
                                        onOpenChapter(
                                          chapter.itemId
                                        )
                                      }
                                    >
                                      {isFR
                                        ? "Ouvrir le chapitre"
                                        : "Open Chapter"}
                                    </button>
                                  ) : null}
                                </div>
                              </article>
                            );
                          }
                        )}
                    </div>
                  </section>
                )
              )}
            </div>
          </>
        )}
      </div>

      {typeof onBackToWriting ===
      "function" ? (
        <div
          style={{
            marginTop: 18,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={
              onBackToWriting
            }
          >
            {isFR
              ? "Retour à Rédiger les chapitres"
              : "Back to Write Chapters"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
